import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Loading from '../Common/Loading';
import close_n from '../Images/close_n.gif';
import close_o from '../Images/close_o.gif';
import close_d from '../Images/close_d.gif';
import done_n from '../Images/done_n.gif';
import done_o from '../Images/done_o.gif';
import done_d from '../Images/done_d.gif';


interface QnDetails {
    qn_no: string; type_name: string; qn_type: string; complexity: string;
    used_in_t: string; used_in_e: string; prc_nos: string;
    no_prc: number; no_clue: number; no_sol: number;
}
interface StudentQuestionData {
    lesson_name: string; total_qns: number; qn_no: string;
    qn_details: QnDetails | null; question_data_string: string;
    content: { question: string; clue: string; principles: string[]; solution: string };
    blob_lesson_path: string; blob_assets_path: string;
    wrong_audio_files: string; correct_audio_files: string;
    country: string; mdcode: string;
    called_from: string; show_back: boolean; show_close: boolean;
    attempts?: Attempt[];
    attended_qn_nos?: string[];
    attended_total?: number;
    student_first_name?: string;
    lesson_code?: string;
    test_code?: string;
    is_test?: boolean;
    attended_items?: { lsn_code: string; qn_no: string }[];
}

interface Attempt {
    SessionId?: string | number;
    AnsweredTime?: string;
    EntCorAns?: string; EntCrtAns?: string;
    EntWrongAns?: string;
    EntBlankAns?: string;
    OptOrder?: string;
}

const GifBtn: React.FC<{
    name: string; onClick?: () => void; visible?: boolean;
    style?: React.CSSProperties; marginLeft?: string;
}> = ({ name, onClick, visible = true, style, marginLeft }) => {
    const [sfx, setSfx] = useState<'n' | 'o' | 'd'>('n');
    if (!visible) return null;

    const imageSrc =
        name === 'close'
            ? (sfx === 'o' ? close_o : sfx === 'd' ? close_d : close_n)
            : name === 'done'
                ? (sfx === 'o' ? done_o : sfx === 'd' ? done_d : done_n)
            : `/Image/${name}_${sfx}.gif`;

    return (
        <img
            src={imageSrc}
            alt={name}
            draggable={false}
            style={{
                cursor: 'pointer',
                border: '0',
                verticalAlign: 'middle',
                marginLeft: marginLeft || '0',
                ...style
            }}
            onMouseEnter={() => setSfx('o')}
            onMouseLeave={() => setSfx('n')}
            onMouseDown={() => setSfx('d')}
            onMouseUp={() => setSfx('o')}
            onClick={onClick}
        />
    );
};

// ---------------------------------------------------------------------
// Small DOM helpers (this component talks to iframe documents directly,
// same as the original jQuery code, just without jQuery).
// ---------------------------------------------------------------------
const qsa = <T extends Element = Element>(root: ParentNode, sel: string): T[] =>
    Array.from(root.querySelectorAll(sel)) as T[];

/**
 * Resolve lesson audio/assets robustly. The legacy code concatenates
 * blobLessonPath + path; this additionally preserves SAS query strings.
 */
const resolveLessonAssetUrl = (base: string, assetPath: string): string => {
    const path = String(assetPath || '').trim();
    if (!path) return base || '';
    if (/^https?:\/\//i.test(path)) return path;

    try {
        const url = new URL(base);
        const cleanPath = url.pathname.endsWith('/') ? url.pathname : `${url.pathname}/`;
        url.pathname = `${cleanPath}${path.replace(/^\/+/, '')}`;
        return url.toString();
    } catch (_) {
        return `${base || ''}${path.replace(/^\/+/, '')}`;
    }
};


const buildSolutionHtml = (html: string, blobBase: string): string => {
    const raw = String(html || '').trim();
    if (!raw) return '';

    try {
        const parser = new DOMParser();
        const parsed = parser.parseFromString(raw, 'text/html');
        const head = parsed.head || parsed.documentElement.appendChild(parsed.createElement('head'));
        const body = parsed.body || parsed.documentElement.appendChild(parsed.createElement('body'));

        // Legacy StudentQuestionViewer.js does not display the source solution
        // table directly. On iframe load it extracts body > table > tr rows and
        // rebuilds a simple two-column table:
        //   column 1 = the student's/worked step (original TD #2)
        //   column 2 = KsdDiv answer panel
        const rows = Array.from(
            body.querySelectorAll(':scope > table > tbody > tr')
        ) as HTMLTableRowElement[];

        const newTable = parsed.createElement('table');
        newTable.setAttribute('border', '1');
        newTable.setAttribute('width', '100%');
        newTable.setAttribute('cellpadding', '4');
        newTable.setAttribute('cellspacing', '0');

        rows.forEach(row => {
            const tds = row.querySelectorAll('td');
            const sourceTd = tds.length > 1 ? tds[1] : null;
            if (!sourceTd) return;

            const contentClone = sourceTd.cloneNode(true) as HTMLElement;
            qsa<HTMLElement>(contentClone, '*').forEach(el => el.removeAttribute('style'));
            contentClone.removeAttribute('style');

            const ksdDiv = row.querySelector('div[id^="KsdDiv"]');
            let ksdHtml = '';
            if (ksdDiv) {
                const ksdClone = ksdDiv.cloneNode(true) as HTMLElement;
                qsa<HTMLElement>(ksdClone, '*').forEach(el => el.removeAttribute('style'));
                ksdClone.removeAttribute('style');
                ksdHtml = ksdClone.outerHTML;
            }

            const newRow = parsed.createElement('tr');

            const lhs = parsed.createElement('td');
            lhs.style.verticalAlign = 'top';
            lhs.innerHTML = contentClone.innerHTML || '';

            const rhs = parsed.createElement('td');
            rhs.style.verticalAlign = 'top';
            rhs.innerHTML = ksdHtml;

            newRow.appendChild(lhs);
            newRow.appendChild(rhs);
            newTable.appendChild(newRow);
        });

        // Exact second load-handler cleanup from the legacy script.
        qsa<HTMLElement>(body, 'p').forEach(p => {
            p.style.margin = '0';
            p.style.padding = '0';
        });
        body.removeAttribute('bgcolor');
        body.style.width = '98.7%';
        body.style.marginLeft = '10px';
        body.style.marginTop = '0';
        body.style.padding = '0';
        body.innerHTML = '';
        body.appendChild(newTable);

        // Preserve the legacy relative asset base. Do this only after the
        // rebuilt table is present so answer images resolve correctly.
        if (blobBase) {
            const normalizedBase = blobBase.endsWith('/') ? blobBase : `${blobBase}/`;
            const existingBase = head.querySelector('base');
            if (existingBase) existingBase.setAttribute('href', normalizedBase);
            else {
                const base = parsed.createElement('base');
                base.setAttribute('href', normalizedBase);
                head.insertBefore(base, head.firstChild);
            }
        }

        // The legacy page executes EL_Sol.js in the source page, but the
        // review transformation itself removes the click/show handlers by
        // cloning only the answer panel's content. Keep the resulting review
        // document passive.
        qsa<HTMLElement>(body, '[onclick*="ShowAns"], [onmouseover*="ShowPic"], [onmouseout*="ShowPic"]')
            .forEach(el => {
                el.removeAttribute('onclick');
                el.removeAttribute('onmouseover');
                el.removeAttribute('onmouseout');
            });

        return '<!DOCTYPE html>' + parsed.documentElement.outerHTML;
    } catch (error) {
        console.warn('StudentQuestionViewer: solution HTML normalization failed; using raw HTML.', error);
        return raw;
    }
};

const correctBg = '#008000';
const wrongBg = '#FF0000';

// ---------------------------------------------------------------------
// New helpers (prefixed with the page name)
// ---------------------------------------------------------------------

/** JS header text: >1 attempts -> "Attempt n (AnsweredTime: x)"; single -> "AnsweredTime: x" or "(AnsweredTime: x)" depending on the type. */
const StudentQuestionViewer_headerText = (idx: number, total: number, answeredTime: any, parenSingle: boolean): string =>
    total > 1
        ? `Attempt ${idx + 1} (AnsweredTime: ${answeredTime})`
        : (parenSingle ? `(AnsweredTime: ${answeredTime})` : `AnsweredTime: ${answeredTime}`);

const attemptHeaderRow = (colCount: number, idx: number, total: number, answeredTime: any, isCorrect: boolean, parenSingle: boolean = false) => {
    const tr = document.createElement('tr');
    const td = document.createElement('td');
    td.colSpan = colCount;
    td.style.fontWeight = 'bold';
    td.style.padding = '8px';
    td.style.background = isCorrect ? correctBg : wrongBg;
    td.style.color = 'white';
    td.textContent = StudentQuestionViewer_headerText(idx, total, answeredTime, parenSingle);
    tr.appendChild(td);
    return tr;
};

const StudentQuestionViewer_clone = (el: Element) => el.cloneNode(true) as HTMLElement;

/** jQuery $(row).find("td").last() — last td in document order (includes nested). */
const StudentQuestionViewer_lastTd = (row: Element): HTMLElement | null => {
    const tds = row.querySelectorAll('td');
    return tds.length ? (tds[tds.length - 1] as HTMLElement) : null;
};

const StudentQuestionViewer_alignImages = (doc: Document, selectors: string[]) => {
    selectors.forEach(sel => qsa<HTMLElement>(doc, sel).forEach(img => img.setAttribute('align', 'absMiddle')));
};

const StudentQuestionViewer_hideInputs = (span: HTMLElement) => {
    qsa<HTMLElement>(span, 'input').forEach(i => { i.style.display = 'none'; });
};

const StudentQuestionViewer_underline = (span: HTMLElement) => {
    span.style.border = 'none';
    span.style.borderBottom = 'solid 2px red';
};

/** Image paths: JS does blobPath + src for every <img> and [background]. Absolute / already-prefixed values are left alone. */
const StudentQuestionViewer_fixAssetPaths = (doc: Document, blobPath: string) => {
    const needsPrefix = (v: string) =>
        !!v && !/^(https?:)?\/\//i.test(v) && !/^data:/i.test(v) && !(blobPath && v.indexOf(blobPath) === 0);
    qsa<HTMLImageElement>(doc, 'img').forEach(img => {
        const src = img.getAttribute('src');
        if (src !== null && needsPrefix(src)) img.setAttribute('src', resolveLessonAssetUrl(blobPath, src));
    });
    qsa<HTMLElement>(doc, '[background]').forEach(el => {
        const bg = el.getAttribute('background');
        if (bg && needsPrefix(bg)) el.setAttribute('background', resolveLessonAssetUrl(blobPath, bg));
    });
};

/** LoadQuestion(): strip legacy attributes from #KSD_TABLE descendants, then give the table a border. */
const StudentQuestionViewer_prepareOptionsTable = (tbl: HTMLElement) => {
    // Clean ONLY wrapper/layout tables. Real content grids (e.g. the ten-frame in
    // Recognise-type questions: a leaf table with several cells) are marked and
    // left exactly as authored, otherwise their columns stretch and borders vanish.
    const attrs = [
        'style', 'width', 'height', 'bgcolor', 'cellspacing', 'cellpadding',
        'align', 'border', 'bordercolor', 'bordercolorlight', 'bordercolordark',
        'colorlight', 'colordark'
    ];

    qsa<HTMLElement>(tbl, 'table').forEach(t => {
        if (!t.querySelector('table') && t.querySelectorAll('td').length > 1) {
            t.setAttribute('data-keep', '1');
        }
    });
    const isKept = (n: Element) => !!n.closest('[data-keep="1"]');

    qsa<HTMLElement>(tbl, 'table, tr, td').forEach(n => {
        if (isKept(n)) return;
        attrs.forEach(a => n.removeAttribute(a));
    });

    if (!tbl.getAttribute('border') || tbl.getAttribute('border') === '0') {
        tbl.setAttribute('border', '1');
        tbl.style.borderCollapse = 'collapse';
    }

    // Legacy only adds the border to the direct option rows.
    qsa<HTMLTableRowElement>(tbl, ':scope > tbody > tr')
        .filter(tr => tr.id.indexOf('KSD_TR_') === 0)
        .forEach(tr => { tr.style.border = '1px solid #000'; });

    qsa<HTMLElement>(tbl, ':scope > tbody > tr[id^="KSD_TR_"] > td').forEach((td, index, cells) => {
        td.style.verticalAlign = 'middle';
        if (cells.length === 2) {
            td.style.width = index === 0 ? '45%' : '55%';
        }
        if (index === 1) td.style.textAlign = 'left';
    });

    // Wrapper tables only: borderless, full width.
    qsa<HTMLTableElement>(tbl, 'table')
        .filter(inner => !isKept(inner))
        .forEach(inner => {
            inner.style.border = '0';
            inner.style.borderCollapse = 'separate';
            inner.style.borderSpacing = '0';
            inner.style.width = '100%';
            inner.style.tableLayout = 'auto';
        });
    qsa<HTMLElement>(tbl, 'table td, table tr')
        .filter(el => !isKept(el))
        .forEach(el => {
            el.style.border = '0';
            el.style.backgroundColor = 'white';
            el.style.backgroundImage = 'none';
        });
};

/** JS imgOK click handler: one-shot resize (fires at 550ms). */
const StudentQuestionViewer_resizeIframe = (iframe: HTMLIFrameElement) => {
    try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc || !doc.body || !doc.documentElement) return;
        const body = doc.body;
        const html = doc.documentElement;
        const newHeight = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
        iframe.style.height = (newHeight + 20) + 'px';
    } catch (_) { }
};

/** N type: render the "correct" entries (index = all but last char, value = last char). */
const StudentQuestionViewer_renderNCorrect = (doc: Document, enteredCorrect: string) => {
    enteredCorrect.split(',')[0].split('~').forEach(fullVal => {
        const idx = parseInt(fullVal.substring(0, fullVal.length - 1), 10);
        const val = fullVal.substring(fullVal.length - 1);
        const input = qsa<HTMLInputElement>(doc, 'input[id^="KSD_BLANK_"]')[idx];
        if (!input) return;
        const span = doc.createElement('span');
        span.style.color = 'green';
        span.style.borderBottom = '2px solid red';
        span.innerHTML = val;
        input.insertAdjacentElement('afterend', span);
        input.style.display = 'none';
    });
};

/** I type: mark a .word-option (border + optional icon). */
const StudentQuestionViewer_markWordOption = (opt: HTMLElement, border: string, iconSrc?: string) => {
    if (iconSrc) opt.insertAdjacentHTML('beforeend', `<img align='absmiddle' width='17px' src='${iconSrc}'/>`);
    opt.style.border = border;
    opt.style.margin = '0 4px';
};

const StudentQuestionViewer: React.FC = () => {
    const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const getParam = useCallback((name: string): string | null => {
        const lower = name.toLowerCase();
        for (const [key, value] of searchParams.entries()) {
            if (key.toLowerCase() === lower) return value;
        }
        return null;
    }, [searchParams]);

    const [data, setData] = useState<StudentQuestionData | null>(null);
    const [loading, setLoading] = useState(true);
    // F type: LoadQuestion() appends these to #tblrhs
    const [fExtra, setFExtra] = useState<{ correct: string; wrong: string; dragDrop: boolean } | null>(null);

    const iframeQnRef = useRef<HTMLIFrameElement>(null);
    const iframeCluRef = useRef<HTMLIFrameElement>(null);
    const iframeSolRef = useRef<HTMLIFrameElement>(null);
    const prcIframeRefs = useRef<(HTMLIFrameElement | null)[]>([]);
    const audioRef = useRef<any>(null);
    const processedDocRef = useRef<Document | null>(null);
    // = $("#hidQndetails").val() in the JS (value of the input whose id ends with "info")
    const qnDetailsRef = useRef<string>('');

    const fromPage = getParam('from') || '';
    const title = getParam('Title') || '';
    const lessonId = getParam('Lessonid') || '';
    const lessonCode = fromPage ? title : lessonId;
    const country = getParam('Country') || 'Australia';
    const mdcode = getParam('mdcode') || '';
    const qnNoStr = getParam('QnNo') || getParam('Qnno') || '1';

    // Exact paths used by the legacy ASPX/JS page (Request.Url authority + /Storage/...).
    // The API's Azure-Blob URLs remain available, but rendering/answer assets use the
    // legacy application paths so the O-type background images and answer images
    // resolve exactly like the VB page.
    const blobLessonCode = data?.lesson_code || getParam('LsnCode') || lessonCode;
    const legacyLessonPath = country === 'MD'
        ? `${window.location.origin}/Storage/mddata/${mdcode}/${blobLessonCode}_E/`
        : `${window.location.origin}/Storage/eldata/DicFiles/${country}/${blobLessonCode}_E/`;
    const legacyAssetsPath = data?.blob_assets_path || `${window.location.origin}/Storage/assets/`;

    // IMPORTANT: the legacy viewer displays the ORIGINAL question number from
    // the lesson/database, including leading zeroes (001, 004, 007, ...).
    const originalQnNo = data?.qn_details?.qn_no || data?.qn_no || qnNoStr;
    const displayQnNo = /^\d+$/.test(String(originalQnNo).trim())
        ? String(parseInt(String(originalQnNo).trim(), 10)).padStart(3, '0')
        : String(originalQnNo).trim();

    const diccode = getParam('DICCode') || getParam('Diccode') || '';
    const testCode = getParam('Lsntestcode') || getParam('TestCode') || '';
    const familyCode = getParam('FamilyCode') || '';
    const firstName = data?.student_first_name || getParam('Login') || getParam('Fname') || '';
    const selectedDiccode = getParam('selectedDiccode') || '';
    const fromlsndate = getParam('Fromlsndate') || '';
    const tolsndate = getParam('Tolsndate') || '';
    const entCrtAnsParam = getParam('EntCorAns') || getParam('EntCrtAns') || '';
    const effectiveDiccode = selectedDiccode || diccode;

    const displayTestCode = testCode || data?.test_code || '';

    const [insightUrls, setInsightUrls] = useState<string[]>([]);
    const [insightIndex, setInsightIndex] = useState(0);

    useEffect(() => {
        setInsightUrls(JSON.parse(sessionStorage.getItem('questionUrls') || '[]'));
        setInsightIndex(parseInt(sessionStorage.getItem('currentIndex') || '0', 10));
    }, []);

    // When Student Performance opens Q1, remember the report URL. Subsequent
    // question-to-question navigation changes document.referrer to the
    // previous question, so history/back cannot reliably return to the report.
    useEffect(() => {
        if (fromPage !== 'EL_Studentperformance') return;

        const key = 'StudentPerformanceReturnUrl';
        const referrer = document.referrer || '';
        const referrerIsViewer = referrer.includes('/LessonBrowser/StudentQuestionViewer');

        if (!sessionStorage.getItem(key) && referrer && !referrerIsViewer) {
            sessionStorage.setItem(key, referrer);
        }
    }, [fromPage]);

    // -----------------------------------------------------------------
    // Attempt history (= window.currentAttempts in the JS)
    // -----------------------------------------------------------------
    const getAttempts = useCallback((): Attempt[] => {
        if (data?.attempts) return data.attempts;
        try {
            let raw = sessionStorage.getItem('attemptData') || '';
            raw = raw.replace(/[\n\r]/g, '\\n');
            const allAttempts = JSON.parse(raw || '{}');
            const normalizedQnNo = String(parseInt(qnNoStr, 10) || qnNoStr);
            const keys = [`${qnNoStr}|${title}`, `${normalizedQnNo}|${title}`, qnNoStr, normalizedQnNo];
            for (const key of keys) {
                if (Array.isArray(allAttempts[key])) return allAttempts[key];
            }
            return [];
        } catch (e) {
            console.error('attemptData parse error:', e);
            return [];
        }
    }, [data, qnNoStr, title]);

    // ---- data fetch (unchanged contract with the backend) ----
    useEffect(() => {
        if (!lessonCode) { setLoading(false); return; }
        setLoading(true);
        processedDocRef.current = null;

        const params = new URLSearchParams({
            Country: country, Qnno: qnNoStr, mdcode, from: fromPage,
        });
        ['SchoolCode', 'FamilyCode', 'Login', 'DICCode', 'SessionID', 'TestCode', 'LsnCode', 'Lsntestcode'].forEach(name => {
            const value = getParam(name);
            if (value) params.set(name, value);
        });
        if (fromPage) params.set('Title', title);
        else params.set('Lessonid', lessonId);

        fetch(`${API_BASE_URL}api/student_question_viewer_get_data?${params.toString()}`)
            .then(r => r.json())
            .then(json => { if (!json.error) setData(json); else console.error(json.error); })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [lessonCode, country, qnNoStr, mdcode, fromPage, title, lessonId, API_BASE_URL, getParam]);

    const play = useCallback((path: string) => {
        const rawPath = String(path || '').trim();
        if (!rawPath) return;

        try {
            if (audioRef.current) {
                try {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                    audioRef.current.src = '';
                } catch (_) { }
            }

            const base = legacyLessonPath;
            let audioUrl = resolveLessonAssetUrl(base, rawPath);

            const audio = new Audio();
            audio.preload = 'auto';
            audio.crossOrigin = 'anonymous';
            audio.src = audioUrl;
            audioRef.current = audio;

            audio.onerror = () => {
                console.error('StudentQuestionViewer audio failed:', {
                    path: rawPath,
                    audioUrl,
                    error: audio.error?.message || audio.error?.code || 'unknown'
                });
            };

            audio.onended = () => {
                if (audioRef.current === audio) {
                    audioRef.current = null;
                }
            };

            audio.load();

            const promise = audio.play();
            if (promise && typeof promise.catch === 'function') {
                promise.catch(error => {
                    console.error('StudentQuestionViewer audio.play() failed:', {
                        path: rawPath,
                        audioUrl,
                        error
                    });
                });
            }
        } catch (error) {
            console.error('StudentQuestionViewer audio error:', error);
        }
    }, [data, legacyLessonPath]);

    const injectPlaySoundBridge = useCallback((iframe: HTMLIFrameElement) => {
        try {
            const win = iframe.contentWindow as any;
            if (!win) return;

            const bridge = (_id: string, path: string) => play(path);

            win.playSound = bridge;

            try {
                win.parent.playSound = bridge;
            } catch (_) { }

            window.setTimeout(() => {
                try {
                    if (iframe.contentWindow) {
                        (iframe.contentWindow as any).playSound = bridge;
                    }
                } catch (_) { }
            }, 0);
        } catch (_) { }
    }, [play]);

    const setIframeHeightToBody = (iframe: HTMLIFrameElement) => {
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc || !doc.body) return;
            iframe.style.height = '0px';
            const contentHeight = doc.body.getBoundingClientRect().height;
            iframe.style.height = (contentHeight + 20) + 'px';
            iframe.style.overflow = 'hidden';
            iframe.setAttribute('scrolling', 'no');
        } catch (_) { }
    };

    // JS adjustIframeHeight() — runs once the images in the rebuilt body have loaded
    const adjustIframeHeight = (iframe: HTMLIFrameElement) => {
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc || !doc.body) return;
            const tableH = (doc.getElementById('tableqn') as HTMLElement)?.offsetHeight || 0;
            const ksdH = (doc.getElementById('ksd_table') as HTMLElement)?.offsetHeight || 0;
            const bodyH = doc.body.scrollHeight || 0;
            iframe.style.height = (Math.max(tableH + ksdH + 25, bodyH + 10) + 30) + 'px';
            iframe.style.overflow = 'hidden';
        } catch (_) { }
    };

    // =====================================================================
    // highlightInAllIframes_DOMSAFE (SearchWords / match query params)
    // =====================================================================
    const StudentQuestionViewer_highlightSearchWords = useCallback((iframe: HTMLIFrameElement) => {
        let text = getParam('SearchWords');
        if (!text) return;
        try { text = decodeURIComponent(text); } catch (_) { }
        text = text.replace(/\s+/g, ' ').trim();
        if (!text) return;

        const matchType = getParam('match');
        const escaped = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = escaped.replace(/\s+/g, '\\s+');
        const regex = matchType === 'Exactmatch'
            ? new RegExp('\\b' + pattern + '\\b', 'gi')
            : new RegExp(pattern, 'gi');

        const run = () => {
            try {
                const doc = iframe.contentDocument;
                if (!doc || !doc.body) return;
                const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
                    acceptNode(node: Node) {
                        if (!(node.nodeValue || '').trim()) return NodeFilter.FILTER_REJECT;
                        const tag = (node.parentNode as Element | null)?.tagName;
                        if (tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT;
                        return NodeFilter.FILTER_ACCEPT;
                    }
                } as any);
                const nodes: Node[] = [];
                let n: Node | null;
                while ((n = walker.nextNode())) {
                    regex.lastIndex = 0;
                    if (regex.test(n.nodeValue || '')) nodes.push(n);
                }
                nodes.forEach(node => {
                    const span = doc.createElement('span');
                    span.innerHTML = (node.nodeValue || '').replace(regex, m =>
                        `<span style="background-color:yellow;color:black;padding:2px;border-radius:3px;">${m}</span>`
                    );
                    node.parentNode?.replaceChild(span, node);
                });
            } catch (_) { }
        };
        window.setTimeout(run, 400);
    }, [getParam]);

    // =====================================================================
    // Init_<Type> — ported from the JS. These run first (like the iframe
    // load handler), then the matching MarkAns_<Type> (like the imgOK click).
    // =====================================================================

    const Init_O = (doc: Document, assetsPath: string) => {
        // Exact Init_O behavior from the legacy StudentQuestionViewer.js.
        // This function is intentionally synchronous; MarkAns_O schedules its
        // own 500ms work, just like the legacy page.
        qsa<HTMLElement>(doc, 'body>table').forEach(t => { t.style.backgroundColor = 'White'; });
        doc.body.style.backgroundColor = 'White';

        const questionDetails = qnDetailsRef.current.split('|');

        const SelectedOptionBGColor = '#7a96df';
        const SelectedOptionTextColor = questionDetails[1] || '#000000';
        const CorrectOptionIndex = questionDetails[3] || '';
        const NoOfOptionsToShow = parseInt(questionDetails[4] || '0', 10) || 0;
        const OptionImageType = questionDetails.length >= 9
            ? (parseInt(questionDetails[8] || '0', 10) || 0)
            : 0;

        let Optionbgcolor = 0;
        if (questionDetails.length >= 10) {
            const style = String(questionDetails[9] || '');
            Optionbgcolor = parseInt(style.substring(0, 1), 10) || 0;
        }

        let BgImageSelected = '';
        let BgImageUnselected = '';

        if (OptionImageType === 2) {
            BgImageUnselected = 'large_objective_n.png';
            BgImageSelected = 'large_objective_d.png';
        } else if (OptionImageType === 3) {
            BgImageUnselected = 'medium_objective_n.png';
            BgImageSelected = 'medium_objective_d.png';
        } else if (OptionImageType === 4) {
            BgImageUnselected = 'small_objective_n.png';
            BgImageSelected = 'small_objective_d.png';
        } else if (OptionImageType === 5) {
            BgImageUnselected = 'large_objective_shortened_n.png';
            BgImageSelected = 'large_objective_shortened_d.png';
        } else if (OptionImageType === 6) {
            BgImageUnselected = 'medium_objective_shortened_n.png';
            BgImageSelected = 'medium_objective_shortened_d.png';
        } else if (OptionImageType === 7) {
            BgImageUnselected = 'small_objective_shortened_n.png';
            BgImageSelected = 'small_objective_shortened_d.png';
        }

        // Legacy Init_O also expands SubNo/VarValues-driven spans.
        const qData = String(data?.question_data_string || '').split('-|-');
        const subNo = qData[2] || '1';
        const varValues = qData[3] || '1';
        if (subNo !== '0' && varValues) {
            varValues.split('^').forEach(item => {
                const parts = item.split('|');
                const variableName = parts[0] || '';
                const variableValue = parts[1];
                const variableStyle = parts[2];

                if (!variableName) return;

                const variableSpan = doc.getElementById(`VAR${variableName}`) as HTMLElement | null;
                if (!variableSpan) return;

                if (variableValue !== undefined) {
                    variableSpan.textContent = variableValue;
                }
                if (variableStyle !== undefined) {
                    variableSpan.setAttribute('style', variableStyle);
                }
            });
        }

        const tbody = doc.querySelector('#KSD_TABLE>tbody') as HTMLTableSectionElement | null;
        if (!tbody) {
            StudentQuestionViewer_alignImages(doc, ['p>font>span>font>img', 'p>span>img']);
            doc.body.removeAttribute('onmousemove');
            return;
        }

        const optionRows = () =>
            qsa<HTMLTableRowElement>(tbody, ':scope > tr[id^="KSD_TR_"]');

        // Legacy randomization pass 1.
        let initialCount = optionRows().length;
        for (let i = 0; i < initialCount; i++) {
            const available = optionRows();
            if (!available.length) break;
            const randomNumber = Math.floor(Math.random() * (initialCount - i));
            const row = available[randomNumber];
            if (row) tbody.appendChild(row);
        }

        // Legacy always puts special rows 24/25 at the end.
        qsa<HTMLTableRowElement>(tbody, ':scope > tr[id^="KSD_TR_24"]').forEach(r => tbody.appendChild(r));
        qsa<HTMLTableRowElement>(tbody, ':scope > tr[id^="KSD_TR_25"]').forEach(r => tbody.appendChild(r));

        // Legacy randomization pass 2 (24/25 never move out of the tail).
        const secondCount = optionRows().length;
        for (let i = 0; i < secondCount; i++) {
            const available = optionRows();
            if (!available.length) break;
            const randomNumber = Math.floor(Math.random() * (secondCount - i));
            const row = available[randomNumber];
            if (row && row.id !== 'KSD_TR_24' && row.id !== 'KSD_TR_25') {
                tbody.appendChild(row);
            }
        }

        qsa<HTMLTableRowElement>(tbody, ':scope > tr[id^="KSD_TR_24"]').forEach(r => tbody.appendChild(r));
        qsa<HTMLTableRowElement>(tbody, ':scope > tr[id^="KSD_TR_25"]').forEach(r => tbody.appendChild(r));

        // Legacy trims until NoOfOptionsToShow remains, never deleting the
        // correct option or special rows 24/25.
        // REVIEW MODE: keep ALL options in the DOM (the student only saw a subset,
        // but the viewer must show every option). Trim is intentionally disabled.
        if (false && NoOfOptionsToShow < optionRows().length) {
            let guard = 0;
            while (NoOfOptionsToShow !== optionRows().length && guard++ < 10000) {
                const rowsNow = optionRows();
                if (!rowsNow.length) break;

                const randomNumber = Math.floor(Math.random() * rowsNow.length);
                const row = rowsNow[randomNumber];
                const radioName = row?.querySelector<HTMLInputElement>('input[id^="KSD_Rad_"]')?.getAttribute('name') || '';

                if (
                    row &&
                    radioName !== CorrectOptionIndex &&
                    row.id !== 'KSD_TR_24' &&
                    row.id !== 'KSD_TR_25'
                ) {
                    row.remove();
                }
            }
        }

        const finalRows = optionRows();

        finalRows.forEach(row => {
            qsa<HTMLInputElement>(row, 'input[id^="KSD_Rad_"]').forEach(radio => {
                radio.style.display = 'none';
            });
            qsa<HTMLElement>(row, ':scope > td[id="KSD_TD1"]').forEach(td => {
                td.style.display = 'none';
            });
        });

        // Legacy stores the actual radio-name order.
        let ShuffledOptionsOrder = '';
        finalRows.forEach(row => {
            const orderNo = row.querySelector<HTMLInputElement>('input[id^="KSD_Rad_"]')?.getAttribute('name') || '';
            if (orderNo) {
                if (ShuffledOptionsOrder !== '') ShuffledOptionsOrder += '~';
                ShuffledOptionsOrder += orderNo;
            }
        });

        // Preserve the legacy return value for parity/debugging.
        (doc.defaultView as any).__StudentQuestionViewer_O_InitResult = {
            ShuffledOptionsOrder,
            NoOfOptions: NoOfOptionsToShow,
        };

        finalRows.forEach((row, index) => {
            const idxFont = row.querySelector<HTMLElement>('td[id="KSD_TD1"] > font[id^="Index"]');
            if (idxFont) idxFont.innerHTML = String.fromCharCode(65 + index);
        });

        // StudentQuestionViewer review mode: VB displays the final answer table
        // on a plain white surface. Do not carry the interactive Objective
        // button background images/alternating fills into the review rows.
        finalRows.forEach(row => {
            row.style.backgroundImage = 'none';
            row.style.backgroundColor = 'white';
            row.style.cursor = 'default';

            qsa<HTMLElement>(row, ':scope > td').forEach(td => {
                td.style.backgroundImage = 'none';
                td.style.backgroundColor = 'white';
            });

            qsa<HTMLElement>(row, ':scope > td > table').forEach(inner => {
                inner.style.backgroundImage = 'none';
                inner.style.backgroundColor = 'white';
            });
        });

        const divider = doc.getElementById('KSD_DIVIDERBAR') as HTMLElement | null;
        if (divider) {
            divider.style.backgroundImage = 'none';
            divider.style.backgroundColor = 'white';
        }

        const optionTable = doc.getElementById('KSD_TABLE') as HTMLElement | null;
        if (optionTable) {
            optionTable.style.backgroundImage = 'none';
            optionTable.style.backgroundColor = 'white';
        }

        if (OptionImageType === 4) {
            qsa<HTMLElement>(doc, '#KSD_TD2 td:first-child').forEach(td => {
                td.style.fontSize = '18px';
            });
        }

        finalRows.forEach(row => {
            qsa<HTMLElement>(row, ':scope > td[id="KSD_TD1"]').forEach(td => {
                td.style.display = 'none';
            });
            qsa<HTMLElement>(row, ':scope > td[id^="KSD_TD_"]').forEach(td => {
                td.style.paddingRight = '20px';
            });
        });

        // Exact legacy row-click selection behavior.
        finalRows.forEach(row => {
            row.onclick = () => {
                finalRows.forEach(otherRow => {
                    qsa<HTMLInputElement>(otherRow, 'input[id^="KSD_Rad_"]').forEach(radio => {
                        radio.checked = false;
                    });
                });

                const currentRadio = row.querySelector<HTMLInputElement>('input[id^="KSD_Rad_"]');
                if (currentRadio) currentRadio.checked = true;

                if (OptionImageType >= 2) {
                    finalRows.forEach(otherRow => {
                        qsa<HTMLElement>(otherRow, ':scope > td[id^="KSD_TD_"]').forEach(td => {
                            td.style.backgroundImage = `url(${assetsPath}Image/Questions/EL_O/${BgImageUnselected})`;
                            td.style.backgroundRepeat = 'no-repeat';
                            td.style.backgroundColor = 'transparent';
                        });
                    });

                    qsa<HTMLElement>(row, ':scope > td[id^="KSD_TD_"]').forEach(td => {
                        td.style.backgroundImage = `url(${assetsPath}Image/Questions/EL_O/${BgImageSelected})`;
                        td.style.backgroundRepeat = 'no-repeat';
                    });
                } else {
                    finalRows.forEach((r, idx) => {
                        r.style.backgroundColor = Optionbgcolor === 1
                            ? 'transparent'
                            : (idx % 2 === 1 ? '#FAF0E6' : '#F8F8F8');
                        r.style.cursor = 'pointer';

                        qsa<HTMLElement>(r, 'td[id="KSD_TD1"] > font[id^="Index"]').forEach(font => {
                            font.style.color = 'red';
                        });
                    });

                    row.style.backgroundColor = SelectedOptionBGColor;
                    qsa<HTMLElement>(row, 'td[id="KSD_TD1"] > font[id^="Index"]').forEach(font => {
                        font.style.color = SelectedOptionTextColor;
                    });
                }
            };
        });

        StudentQuestionViewer_alignImages(doc, ['p>font>span>font>img', 'p>span>img']);
        doc.body.removeAttribute('onmousemove');
    };
    const Init_B = (doc: Document) => {
        // Exact Init_B DOM work. The old page additionally attached this same
        // hide operation to a subsequent iframe load; React's onLoad is already
        // the equivalent lifecycle point.
        qsa<HTMLElement>(doc, 'body>table').forEach(t => { t.style.backgroundColor = 'White'; });
        doc.body.style.backgroundColor = 'White';
        qsa<HTMLElement>(doc, '#KSD_TABLE > tbody > tr[id^="KSD_TR_"] td[id^="KSD_TD1"]')
            .forEach(td => { td.style.display = 'none'; });
        doc.body.removeAttribute('onmousemove');
    };
    const Init_C = (doc: Document) => {
        window.setTimeout(() => {
            const OptOrder = getParam('OptOrder') || '';
            const tbody = doc.querySelector('#KSD_TABLE>tbody') as HTMLTableSectionElement | null;
            if (!tbody) return;

            const questionDetails = qnDetailsRef.current.split('|');
            const BGColorOdd = questionDetails[1] || '';
            const BGColorEven = questionDetails[2] || '';
            const SelectedOptionBGColor = '#7a96df';

            let rows = qsa<HTMLTableRowElement>(tbody, 'tr[id^="KSD_TR_"]');
            rows.forEach(r => r.remove());

            if (OptOrder) {
                const order = OptOrder.split('~').map(v => parseInt(v, 10));
                order.forEach(idx => {
                    if (rows[idx]) tbody.appendChild(rows[idx]);
                });
            } else {
                for (let i = rows.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    const tmp = rows[i];
                    rows[i] = rows[j];
                    rows[j] = tmp;
                }
                rows.forEach(r => tbody.appendChild(r));
            }

            const finalRows = qsa<HTMLTableRowElement>(tbody, 'tr[id^="KSD_TR_"]');

            let ShuffledOptionsOrder = '';
            finalRows.forEach((row, index) => {
                const ind = row.querySelector<HTMLElement>('font[id^="Ind_"]');
                if (ind) ind.innerHTML = String.fromCharCode(65 + index);

                const idNo = row.id.replace('KSD_TR_', '');
                if (ShuffledOptionsOrder !== '') ShuffledOptionsOrder += '~';
                ShuffledOptionsOrder += idNo;

                row.style.backgroundColor = index % 2 === 1 ? BGColorOdd : BGColorEven;
                row.style.cursor = 'pointer';
            });

            (doc.defaultView as any).__StudentQuestionViewer_C_InitResult = {
                ShuffledOptionsOrder,
                NoOfOptions: finalRows.length,
            };

            qsa<HTMLInputElement>(doc, '#KSD_TABLE>tbody>tr[id^="KSD_TR_"] input[id^="Chk_"]')
                .forEach((checkbox, index) => {
                    const row = checkbox.closest<HTMLTableRowElement>('tr[id^="KSD_TR_"]');
                    if (!row) return;

                    checkbox.onclick = (event) => {
                        event.stopPropagation();

                        if (checkbox.checked) {
                            row.style.backgroundColor = SelectedOptionBGColor;
                        } else {
                            // Mirrors the legacy closure's `index`.
                            row.style.backgroundColor = index % 2 === 0 ? BGColorEven : BGColorOdd;
                        }
                    };
                });

            finalRows.forEach(row => {
                row.onclick = () => {
                    const checkbox = row.querySelector<HTMLInputElement>('input[id^="Chk_"]');
                    if (checkbox) checkbox.click();
                };
            });

            qsa<HTMLElement>(doc, '#KSD_TABLE>tbody>tr[id^="KSD_TR_"]>td[id="KSD_TD1"]')
                .forEach(td => { td.style.display = 'none'; });

            doc.body.removeAttribute('onmousemove');
        }, 500);
    };
    const Init_F = (doc: Document) => {
        // Exact synchronous setup from legacy Init_F.
        const body = doc.body;
        const loadedhtml = body.innerHTML;
        const pos = loadedhtml.indexOf('<select id="KSD_SELECT"');
        if (pos !== -1) {
            const pos1 = loadedhtml.indexOf('>', pos) + 1;
            const selectTag = loadedhtml.substring(pos, pos1);
            if (
                selectTag.indexOf('style="') !== -1 &&
                selectTag.toLowerCase().includes('display: none;')
            ) {
                body.innerHTML = loadedhtml.substring(0, pos) + loadedhtml.substring(pos1);
            }
        }

        qsa<HTMLElement>(doc, 'body>table').forEach(t => { t.style.backgroundColor = 'White'; });
        body.style.backgroundColor = 'White';

        // PGM-428: blank width = 15 + 10 * answer length.
        window.setTimeout(() => {
            const correctValue = (doc.getElementById('KSD_BW') as HTMLInputElement | null)?.value || '';
            if (!correctValue) return;

            const CorrectAnswers = correctValue.split('|');
            qsa<HTMLInputElement>(doc, 'input[id^="KSD_BLANK_"]').forEach((input, i) => {
                const answer = CorrectAnswers[i] || '';
                input.style.width = (15 + answer.length * 10) + 'px';
            });
        }, 500);

        qsa<HTMLInputElement>(doc, 'input[id^="KSD_BLANK_"]').forEach(input => {
            Object.assign(input.style, {
                backgroundColor: 'transparent',
                color: '#017ebc',
                fontSize: '18pt',
                fontWeight: '',
                textAlign: 'center',
                cursor: 'pointer',
                fontFamily: 'Verdana',
            });

            input.onpaste = event => {
                event.preventDefault();
            };
        });

        const firstBlank = qsa<HTMLInputElement>(doc, 'input[id^="KSD_BLANK_"]')[0];
        if (firstBlank) {
            try { firstBlank.click(); } catch (_) { }
        }

        StudentQuestionViewer_alignImages(doc, [
            'p>font>span>font>img',
            'p>span>img',
            'p>span>strong>font>img',
            'p>strong>font>span>strong>font>img',
            'p>font>span>img',
        ]);
    };
    const Init_N = (_doc: Document) => {
        // Legacy Init_N only returns the empty result object.
    };
    const Init_W = (doc: Document) => {
        window.setTimeout(() => {
            try {
                (doc.defaultView as any).__StudentQuestionViewer_W_InitResult = {
                    Status: 1,
                    AnswerComment: 'Wrong answer displayed.',
                };
            } catch (_) { }
        }, 500);
    };
    const Init_D = (doc: Document) => {
        window.setTimeout(() => {
            const info = ((doc.getElementById('KSD_MW_Info') as HTMLInputElement | null)?.value || '').split('|');
            const NoOfOptions = parseInt(info[3] || '0', 10) || 0;
            let ShuffledOptionsOrder = '';

            qsa<HTMLSelectElement>(doc, "table select[id='KSD_SELECT']").forEach((select, index) => {
                const options = Array.from(select.options);
                const orderIndexes = options.map((_, i) => i);

                while (select.options.length) select.remove(0);

                let order = '';
                while (orderIndexes.length) {
                    const rnd = Math.floor(Math.random() * orderIndexes.length);
                    const sourceIndex = orderIndexes[rnd];
                    const option = options[sourceIndex];
                    if (option) select.appendChild(option);

                    order += sourceIndex < 10 ? `0${sourceIndex}` : String(sourceIndex);
                    orderIndexes.splice(rnd, 1);
                }

                if (ShuffledOptionsOrder !== '') ShuffledOptionsOrder += ',';
                ShuffledOptionsOrder += `${index}-${order}`;
            });

            try {
                (doc.defaultView as any).__StudentQuestionViewer_D_InitResult = {
                    ShuffledOptionsOrder,
                    NoOfOptions,
                };
            } catch (_) { }
        }, 500);
    };
    const Init_I = (doc: Document) => {
        qsa<HTMLElement>(doc, 'body>table').forEach(t => { t.style.backgroundColor = 'White'; });
        doc.body.style.backgroundColor = 'White';

        window.setTimeout(() => {
            const infoVal = (doc.getElementById('KSD_MW_Info') as HTMLInputElement | null)?.value || '';
            const q = infoVal.split('|');
            const NoOfOptions = parseInt(q[0] || '0', 10) || 0;

            // Parsed in the legacy code even though TextColor itself is not used.
            const TextColor = q[NoOfOptions + 1] || '#000000';
            void TextColor;

            const MouseoverOptionBorderColor = q[NoOfOptions + 2] || '#999999';
            const SelectedOptionBorderColor = q[NoOfOptions + 3] || '#006600';
            const FontSize = q[NoOfOptions + 4] || '12';
            const FontFamily = q[NoOfOptions + 5] || 'Arial';

            const WordOptions: string[] = [];
            for (let i = 0; i < NoOfOptions; i++) {
                WordOptions[i] = q[i + 1] || '0N';
            }

            let html = `<center><font style='font-family:${FontFamily};font-size:${FontSize}pt;'>`;
            for (let i = 0, j = 1; i < NoOfOptions; i++) {
                const opt = WordOptions[i];
                if (opt === '0N<NEW LINE>') {
                    html += '<br>';
                } else if (opt && (opt.substring(0, 1) === '1' || opt.substring(0, 1) === '2')) {
                    html += `<span id='spanWordOption${j}' class='word-option'>&nbsp;${opt.substring(2) || ''}&nbsp;</span>`;
                    j++;
                }
            }
            html += '</font></center>';

            const tdTypeI = doc.getElementById('KSD_TDTYPEI');
            if (tdTypeI) {
                tdTypeI.innerHTML = html;
                tdTypeI.style.border = 'none';
            }

            const tblTypeI = doc.getElementById('KSD_TBLTYPEI');
            if (tblTypeI) {
                const parentTable = tblTypeI.parentElement?.closest('table') as HTMLElement | null;
                if (parentTable) {
                    parentTable.removeAttribute('width');
                    parentTable.style.width = '100%';
                }
                tblTypeI.setAttribute('align', 'center');
            }

            const options = qsa<HTMLElement>(doc, '.word-option');

            options.forEach(option => {
                option.onclick = () => {
                    if (option.getAttribute('title') === 'selected') {
                        option.style.border = 'none';
                        option.setAttribute('title', '');
                    } else {
                        option.style.border = `solid 2px ${SelectedOptionBorderColor}`;
                        option.setAttribute('title', 'selected');
                    }
                };

                option.onmouseover = () => {
                    if (option.getAttribute('title') !== 'selected') {
                        option.style.border = `solid 2px ${MouseoverOptionBorderColor}`;
                        option.style.cursor = 'pointer';
                    }
                };

                option.onmouseout = () => {
                    if (option.getAttribute('title') !== 'selected') {
                        option.style.border = 'none';
                    }
                };
            });

            StudentQuestionViewer_alignImages(doc, ['p>font>span>font>img', 'p>span>img']);
        }, 400);
    };
    const Init_M = (doc: Document, attempts: Attempt[]) => {
        window.setTimeout(() => {
            const table = doc.querySelector('#KSD_TABLE') as HTMLElement | null;
            if (!table) return;

            if (!attempts.length) {
                const EnteredWrongAnswer = getParam('EntWrongAns') || '';
                const OptOrder = getParam('OptOrder') || '';
                const tbody = table.querySelector('tbody');
                if (!OptOrder || !tbody) return;
                void EnteredWrongAnswer;
            }

            const firstRow = table.querySelector('tr');
            const colCount = firstRow?.querySelectorAll('td').length || 2;
            const tbody = table.querySelector('tbody');
            if (!tbody) return;

            const originalRows = qsa<HTMLElement>(tbody, 'tr').map(StudentQuestionViewer_clone);
            tbody.innerHTML = '';

            attempts.forEach((attempt, attemptIndex) => {
                const attemptTbody = doc.createElement('tbody');
                let rows = originalRows.map(StudentQuestionViewer_clone);

                const OptOrder = attempt.OptOrder || '';
                const EnteredWrongAnswer = attempt.EntWrongAns || '';

                if (OptOrder) {
                    const originalLHS: Record<number, HTMLElement> = {};
                    const originalRHS: Record<number, HTMLElement> = {};

                    rows.forEach(row => {
                        const lhs = row.querySelector<HTMLElement>("td[id^='LHS2_']");
                        const rhs = row.querySelector<HTMLElement>("td[id^='RHS2_']");

                        if (lhs) {
                            const lhsIdx = parseInt(lhs.id.replace('LHS2_', ''), 10);
                            originalLHS[lhsIdx] = StudentQuestionViewer_clone(lhs);
                        }

                        if (rhs) {
                            const rhsIdx = parseInt(rhs.id.replace('RHS2_', ''), 10);
                            originalRHS[rhsIdx] = StudentQuestionViewer_clone(rhs);
                        }
                    });

                    const lhsOrder = OptOrder.split('~')[0].split(',').map(Number);
                    const rhsOrder = EnteredWrongAnswer
                        ? (EnteredWrongAnswer.indexOf('~') > -1
                            ? EnteredWrongAnswer.split('~').map(Number)
                            : EnteredWrongAnswer.split(',').map(Number))
                        : lhsOrder.slice();

                    rows = [];

                    for (let i = 0; i < lhsOrder.length; i++) {
                        const lhs = originalLHS[lhsOrder[i]];
                        const rhs = originalRHS[rhsOrder[i]];
                        if (!lhs || !rhs) continue;

                        const tr = doc.createElement('tr');
                        tr.appendChild(StudentQuestionViewer_clone(lhs));
                        tr.appendChild(StudentQuestionViewer_clone(rhs));
                        rows.push(tr);
                    }
                }

                const isCorrect = !attempt.EntWrongAns || attempt.EntWrongAns.trim() === '';
                const answeredTime = attempts.length > 1 ? attempt.AnsweredTime : undefined;

                attemptTbody.appendChild(
                    attemptHeaderRow(
                        colCount,
                        attemptIndex,
                        attempts.length,
                        answeredTime,
                        isCorrect,
                        true
                    )
                );

                rows.forEach(row => attemptTbody.appendChild(row));
                table.appendChild(attemptTbody);
            });
        }, 500);
    };
    const Init_L = (doc: Document, assetsPath: string, attempts: Attempt[]) => {
        window.setTimeout(() => MarkAns_L(doc, assetsPath, attempts), 300);
    };

    const MarkAns_O = (doc: Document, assetsPath: string, attempts: Attempt[]) => {
        const q = qnDetailsRef.current.split('|');
        const correctIdx = parseInt(q[3], 10);

        // Column widths of the review table (percent). Must add up to 100.
        const LETTER_W = 6;
        const CONTENT_W = 50;
        const RESULT_W = 44;

        window.setTimeout(() => {
            if (!attempts.length) return;
            const table = doc.querySelector('#KSD_TABLE') as HTMLElement | null;
            const tbody = doc.querySelector('#KSD_TABLE>tbody') as HTMLElement | null;
            if (!table || !tbody) return;

            const originalRows = qsa<HTMLElement>(tbody, ':scope > tr[id^="KSD_TR_"]')
                .map(StudentQuestionViewer_clone);
            tbody.innerHTML = '';

            // Row lookup by ORIGINAL option index (OptOrder is based on these ids,
            // not on whatever random order Init_O produced).
            const rowById: Record<number, HTMLElement> = {};
            originalRows.forEach(r => {
                const n = parseInt(r.id.replace('KSD_TR_', ''), 10);
                if (!Number.isNaN(n)) rowById[n] = r;
            });
            const sortedIds = Object.keys(rowById).map(Number).sort((a, b) => a - b);

            // Full width, same as the "Question Contents" box.
            const wrapper = doc.getElementById('divOptions') as HTMLElement | null;
            if (wrapper) {
                wrapper.style.display = 'block';
                wrapper.style.width = '100%';
            }
            table.removeAttribute('width');
            table.style.width = '100%';
            table.style.boxSizing = 'border-box';
            table.style.tableLayout = 'fixed';
            table.style.borderCollapse = 'collapse';
            table.style.backgroundColor = 'white';
            table.style.backgroundImage = 'none';
            table.querySelector('colgroup')?.remove();
            table.insertAdjacentHTML(
                'afterbegin',
                `<colgroup>` +
                `<col style="width:${LETTER_W}%">` +
                `<col style="width:${CONTENT_W}%">` +
                `<col style="width:${RESULT_W}%">` +
                `</colgroup>`
            );

            const correctImg = `<img src='${assetsPath}Image/Questions/EL_O/AnsCorrect.GIF' />`;
            const wrongImg = `<img src='${assetsPath}Image/Questions/EL_O/AnsWrong.GIF' />`;

            // Real content grids (ten-frames etc.) are tagged data-keep by prepareOptionsTable.
            const isKept = (n: Element) => !!n.closest('[data-keep="1"]');

            attempts.forEach((attempt, attemptIndex) => {
                // 1) work out the display order for this attempt
                let orderIds = sortedIds.slice();
                if (attempt.OptOrder) {
                    const order = attempt.OptOrder
                        .split('~')
                        .map(v => parseInt(v.trim(), 10))
                        .filter(v => !Number.isNaN(v) && rowById[v] !== undefined);
                    const rest = sortedIds.filter(id => !order.includes(id));
                    orderIds = [...order, ...rest];
                }

                const rawWrong = (attempt.EntWrongAns || '').toString().trim();
                const hasWrong = rawWrong !== '' && rawWrong.toUpperCase() !== 'NULL';
                const wrongOriginal = hasWrong ? parseInt(rawWrong, 10) : null;
                const isCorrectAttempt = !hasWrong;

                // 2) header row (3 visible columns)
                tbody.appendChild(
                    attemptHeaderRow(3, attemptIndex, attempts.length, attempt.AnsweredTime, isCorrectAttempt)
                );

                // 3) rebuild every row as: [letter][option content][result icon]
                orderIds.forEach((id, position) => {
                    const src = rowById[id];
                    if (!src) return;

                    // The real option content lives in KSD_TD2 (fallback: first non-letter cell).
                    const contentSrc =
                        (src.querySelector(':scope > td[id="KSD_TD2"]') as HTMLElement | null) ||
                        (Array.from(src.children).filter(c => c.tagName === 'TD' && c.id !== 'KSD_TD1')[0] as HTMLElement | undefined) ||
                        null;
                    if (!contentSrc) return;

                    const tr = doc.createElement('tr');
                    tr.id = src.id;
                    tr.style.backgroundColor = 'white';
                    tr.style.backgroundImage = 'none';
                    tr.style.cursor = 'default';
                    tr.onclick = null;

                    // Letter column: A/B/C by the order the student saw in this attempt.
                    const letterTd = doc.createElement('td');
                    letterTd.textContent = String.fromCharCode(65 + position);
                    Object.assign(letterTd.style, {
                        padding: '7px 4px',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        fontFamily: 'Verdana, sans-serif',
                        fontSize: '18px',
                        color: 'red',
                        backgroundColor: 'white',
                        backgroundImage: 'none',
                        border: '1px solid #000',
                    });

                    const contentTd = doc.createElement('td');
                    contentTd.innerHTML = contentSrc.innerHTML;
                    Object.assign(contentTd.style, {
                        padding: '7px 8px',
                        verticalAlign: 'middle',
                        backgroundColor: 'white',
                        backgroundImage: 'none',
                        border: '1px solid #000',
                    });

                    // Wrapper tables only: borderless, full width.
                    // Content grids (data-keep) are left exactly as authored.
                    qsa<HTMLElement>(contentTd, 'table')
                        .filter(inner => !isKept(inner))
                        .forEach(inner => {
                            inner.removeAttribute('width');
                            Object.assign(inner.style, {
                                width: '100%',
                                border: '0',
                                borderCollapse: 'collapse',
                                backgroundColor: 'white',
                                backgroundImage: 'none',
                            });
                        });
                    qsa<HTMLElement>(contentTd, 'table td, table tr')
                        .filter(el => !isKept(el))
                        .forEach(el => {
                            el.style.border = '0';
                            el.style.backgroundColor = 'white';
                            el.style.backgroundImage = 'none';
                        });

                    const resultTd = doc.createElement('td');
                    Object.assign(resultTd.style, {
                        padding: '7px 12px',
                        verticalAlign: 'middle',
                        textAlign: 'left',
                        backgroundColor: 'white',
                        backgroundImage: 'none',
                        border: '1px solid #000',
                    });
                    if (wrongOriginal !== null && id === wrongOriginal) resultTd.innerHTML = wrongImg;
                    if (id === correctIdx) resultTd.innerHTML = correctImg;

                    tr.appendChild(letterTd);
                    tr.appendChild(contentTd);
                    tr.appendChild(resultTd);
                    tbody.appendChild(tr);
                });
            });
        }, 500);
    };

    const MarkAns_B = (doc: Document, attemptsIn: Attempt[]) => {
        window.setTimeout(() => {
            const table = doc.querySelector('#KSD_TABLE') as HTMLElement | null;
            if (table) table.style.width = '100%';
            const info = ((doc.getElementById('KSD_MW_Info') as HTMLInputElement | null)?.value || '').split('|');
            const correctIdx = parseInt(info[4], 10);

            let attempts = attemptsIn;
            const tbody = doc.querySelector('#KSD_TABLE>tbody');
            if (!tbody) return;
            const originalRows = qsa<HTMLElement>(tbody, 'tr[id^="KSD_TR_"]').map(StudentQuestionViewer_clone);
            tbody.innerHTML = '';
            const colCount = originalRows[0]?.children.length || 2;

            if (!attempts.length) attempts = [{ EntWrongAns: undefined, SessionId: undefined }];

            attempts.forEach((attempt, attemptIndex) => {
                const rows = originalRows.map(StudentQuestionViewer_clone);
                const rawWrong = (attempt.EntWrongAns || '').toString().trim();
                let wrongIndex: number | null = null;
                if (rawWrong !== '' && rawWrong.toUpperCase() !== 'NULL') {
                    const parsed = parseInt(rawWrong, 10);
                    if (!isNaN(parsed)) wrongIndex = parsed;
                }
                const isCorrect = !rawWrong || rawWrong.toUpperCase() === 'NULL';

                rows.forEach(row => {
                    const rowIdx = parseInt(row.id.replace('KSD_TR_', ''), 10);
                    const td = StudentQuestionViewer_lastTd(row);
                    if (!td) return;
                    td.innerHTML = '';
                    const optionText = rowIdx === 0 ? 'True' : 'False';
                    const isWrongSelected = wrongIndex !== null && rowIdx === wrongIndex;
                    if (isWrongSelected) {
                        td.innerHTML = optionText + " <span style='color:red;font-weight:bold;'>\u2716</span>";
                    } else if (rowIdx === correctIdx) {
                        td.innerHTML = optionText + " <span style='color:green;font-weight:bold;'>\u2714</span>";
                    }
                });

                tbody.appendChild(attemptHeaderRow(colCount, attemptIndex, attempts.length, attempt.AnsweredTime, isCorrect, true));
                rows.forEach(r => tbody.appendChild(r));
            });
        }, 400);
    };
    const MarkAns_C = (doc: Document, assetsPath: string, attempts: Attempt[]) => {
        window.setTimeout(() => {
            const table = doc.querySelector('#KSD_TABLE');
            if (!table || !attempts.length) return;
            const originalRows = qsa<HTMLElement>(table, 'tbody tr[id^="KSD_TR_"]').map(StudentQuestionViewer_clone);
            qsa(table, 'tbody').forEach(tb => tb.remove());
            const colCount = originalRows[0]?.children.length || 2;

            const infoVal = (doc.getElementById('KSD_MW_Info') as HTMLInputElement | null)?.value || '';
            const qParts = infoVal.split('|');
            const correctIdx: string[] = [];
            if (qParts.length > 4 && qParts[4]) {
                for (let i = 0; i < qParts[4].length; i++) correctIdx.push((qParts[4].charCodeAt(i) - 65) + '');
            }

            attempts.forEach((attempt, attemptIndex) => {
                const attemptTbody = doc.createElement('tbody');
                let rows = originalRows.map(StudentQuestionViewer_clone);
                if (attempt.OptOrder) {
                    const order = attempt.OptOrder.split('~').map(Number);
                    const reordered: HTMLElement[] = [];
                    order.forEach(idx => { if (rows[idx]) reordered.push(rows[idx]); });
                    rows.forEach((row, i) => { if (!order.includes(i)) reordered.push(row); });
                    rows = reordered;
                }

                let selectedCorrect: string[] = [];
                let selectedWrong: string[] = [];
                const entCrt = (attempt.EntCorAns || attempt.EntCrtAns || '').toString().trim();
                const entWrong = (attempt.EntWrongAns || '').toString().trim();
                if (entCrt) {
                    const parts = entCrt.split('#');
                    selectedCorrect = parts[1] ? parts[1].split('~') : [];
                    if (selectedCorrect.length === 1 && selectedCorrect[0].length > 1) selectedCorrect = selectedCorrect[0].split('');
                }
                if (entWrong && entWrong.toUpperCase() !== 'NULL') selectedWrong = entWrong.split('~');

                rows.forEach(row => {
                    const idx = row.id.replace('KSD_TR_', '');
                    let imgSrc = '';
                    if (selectedCorrect.includes(idx)) imgSrc = assetsPath + 'Image/Questions/EL_C/AnsCorrect.GIF';
                    else if (correctIdx.includes(idx)) imgSrc = assetsPath + 'Image/Questions/EL_C/AnsShouldbe.GIF';
                    else if (selectedWrong.includes(idx)) imgSrc = assetsPath + 'Image/Questions/EL_C/AnsWrong.GIF';
                    if (imgSrc) {
                        const td = StudentQuestionViewer_lastTd(row);
                        if (td) {
                            qsa(td, '.ans-img').forEach(old => old.remove());
                            td.insertAdjacentHTML('afterbegin',
                                "<img class='ans-img' style='margin-left:6px;width:16px;height:16px;vertical-align:middle;' src='" + imgSrc + "' />");
                        }
                    }
                });

                const isCorrect = selectedWrong.length === 0 && selectedCorrect.length === correctIdx.length;
                attemptTbody.appendChild(attemptHeaderRow(colCount, attemptIndex, attempts.length, attempt.AnsweredTime, isCorrect));
                rows.forEach(r => attemptTbody.appendChild(r));
                table.appendChild(attemptTbody);
            });
        }, 500);
    };
    const MarkAns_M = (doc: Document, assetsPath: string) => {
        window.setTimeout(() => {
            qsa(doc, '#KSD_TABLE > tbody').forEach(tbody => {
                qsa<HTMLElement>(tbody, 'tr').forEach(row => {
                    const lhs = row.querySelector("td[id^='LHS']");
                    const rhs = row.querySelector("td[id^='RHS']");
                    if (!lhs || !rhs || !lhs.id || !rhs.id) return;
                    const lhsIndex = lhs.id.split('_').pop();
                    const rhsIndex = rhs.id.split('_').pop();
                    row.querySelectorAll("td[id^='TDANS_']").forEach(td => td.remove());
                    const ansTd = doc.createElement('td');
                    ansTd.id = 'TDANS_';
                    ansTd.innerHTML = lhsIndex === rhsIndex
                        ? `<img src='${assetsPath}Image/Questions/EL_M/AnsCorrect.GIF'/>`
                        : `<img src='${assetsPath}Image/Questions/EL_M/AnsWrong.GIF'/>`;
                    row.appendChild(ansTd);
                });
            });
        }, 500);
    };
    const MarkAns_F = (doc: Document, assetsPath: string, attemptsIn: Attempt[]) => {
        window.setTimeout(() => {
            const infoVal = (doc.getElementById('KSD_MW_Info') as HTMLInputElement | null)?.value || '';
            const bwVal = (doc.getElementById('KSD_BW') as HTMLInputElement | null)?.value || '';

            if (!infoVal || !bwVal) return;

            const CorrectAnswers = bwVal.split('|');
            const body = doc.body;
            if (!body) return;

            // No attempt history: fall back to the querystring (same as MarkAns_L / MarkAns_N).
            // If the querystring has nothing either, leave the question exactly as rendered
            // instead of wiping the body.
            let attempts = attemptsIn;
            if (!attempts.length) {
                const qsCorrect = getParam('EntCorAns') || getParam('EntCrtAns') || '';
                const qsWrong = getParam('EntWrongAns') || '';
                const qsBlank = getParam('EntBlankAns') || '';
                if (!qsCorrect && !qsWrong && !qsBlank) return;
                attempts = [{
                    EntCorAns: qsCorrect,
                    EntWrongAns: qsWrong,
                    EntBlankAns: qsBlank,
                    SessionId: '',
                }];
            }

            // Legacy MarkAns_F moves "Dummy words:" out while rebuilding
            // attempt-wise content, then appends it back once at the end.
            const dummyHeader = qsa<HTMLElement>(body, 'h4')
                .find(h => (h.textContent || '').trim() === 'Dummy words:') || null;
            const dummyList = dummyHeader?.nextElementSibling?.tagName === 'UL'
                ? dummyHeader.nextElementSibling as HTMLElement
                : null;

            const dummyBlock: HTMLElement[] = [];
            if (dummyHeader) dummyBlock.push(StudentQuestionViewer_clone(dummyHeader));
            if (dummyList) dummyBlock.push(StudentQuestionViewer_clone(dummyList));

            if (dummyHeader) dummyHeader.remove();
            if (dummyList) dummyList.remove();

            const originalHtml = body.innerHTML;
            body.innerHTML = '';

            attempts.forEach((attempt, index) => {
                const EntCrt = (attempt.EntCorAns || attempt.EntCrtAns || '').toString().trim();
                const EntWrong = (attempt.EntWrongAns || '').toString().trim();
                const EntBlank = (attempt.EntBlankAns || '').toString().trim();

                const totalBlanks = CorrectAnswers.length;
                const correctCount = EntCrt ? EntCrt.split('~').length : 0;

                const isCorrect =
                    !EntWrong &&
                    !EntBlank &&
                    correctCount === totalBlanks;

                const bgColor = isCorrect ? correctBg : wrongBg;

                const wrapper = doc.createElement('div');
                wrapper.style.marginBottom = '20px';

                if (attempts.length > 1) {
                    const header = doc.createElement('div');
                    header.style.cssText =
                        `font-weight:bold;margin-bottom:6px;padding:6px 10px;background:${bgColor};color:white;`;
                    header.textContent =
                        `Attempt ${index + 1} (AnsweredTime: ${attempt.AnsweredTime})`;
                    wrapper.appendChild(header);
                }

                if (attempts.length === 1 && attempt.AnsweredTime) {
                    const header = doc.createElement('div');
                    header.style.cssText =
                        `font-weight:bold;margin-bottom:6px;padding:6px 10px;background:${bgColor};color:white;`;
                    header.textContent = `AnsweredTime: ${attempt.AnsweredTime}`;
                    wrapper.appendChild(header);
                }

                const content = doc.createElement('div');
                content.innerHTML = originalHtml;
                const spans = () => qsa<HTMLElement>(content, 'span[id^="span_"]');

                if (EntCrt && !EntWrong && !EntBlank) {
                    const arr = EntCrt.split('~');

                    spans().forEach((span, i) => {
                        StudentQuestionViewer_hideInputs(span);
                        StudentQuestionViewer_underline(span);

                        const val = arr[i] ? arr[i].substring(2) : '';
                        span.insertAdjacentHTML(
                            'beforeend',
                            `<span style='color:green;font-family:Verdana'>${val}</span>` +
                            `<img src='${assetsPath}Image/Questions/EL_O/AnsCorrect.GIF' style='vertical-align:middle;'>`
                        );
                    });
                } else {
                    if (EntCrt) {
                        EntCrt.split('~').forEach(v => {
                            const idx = parseInt(v.substring(0, 2), 10);
                            const target = spans()[idx];
                            if (!target) return;

                            StudentQuestionViewer_hideInputs(target);
                            StudentQuestionViewer_underline(target);

                            target.insertAdjacentHTML(
                                'beforeend',
                                `<span style='color:green'>${v.substring(2)}</span>` +
                                `<img src='${assetsPath}Image/Questions/EL_O/AnsCorrect.GIF'>`
                            );
                        });
                    }

                    if (EntWrong) {
                        EntWrong.split('~').forEach(v => {
                            const idx = parseInt(v.substring(0, 2), 10);
                            const target = spans()[idx];
                            if (!target) return;

                            StudentQuestionViewer_hideInputs(target);
                            StudentQuestionViewer_underline(target);

                            target.insertAdjacentHTML(
                                'beforeend',
                                `<span style='color:red;text-decoration:line-through'>${v.substring(2)}</span> ` +
                                `<span style='color:green'>${CorrectAnswers[idx] ?? ''}</span>`
                            );
                        });
                    }

                    if (EntBlank) {
                        EntBlank.split('~').forEach(v => {
                            const idx = parseInt(v, 10);
                            const target = spans()[idx];
                            if (!target) return;

                            StudentQuestionViewer_underline(target);
                            target.insertAdjacentHTML(
                                'beforeend',
                                `<span style='color:green'>${CorrectAnswers[idx] ?? ''}</span>`
                            );
                        });
                    }
                }

                wrapper.appendChild(content);
                body.appendChild(wrapper);
            });

            dummyBlock.forEach(el => body.appendChild(el));
        }, 500);
    };
    const MarkAns_L = (doc: Document, assetsPath: string, attemptsIn: Attempt[]) => {
        let attempts = attemptsIn;
        const originalHtml = doc.body.innerHTML;

        if (!attempts.length) {
            attempts = [{
                EntCorAns: getParam('EntCorAns') || '',
                EntWrongAns: getParam('EntWrongAns') || '',
                OptOrder: getParam('OptOrder') || '',
                SessionId: '',
            }];
        }

        // split question / table: question shown once, table once per attempt
        const temp = doc.createElement('div');
        temp.innerHTML = originalHtml;
        const table = temp.querySelector('#KSD_TABLE');
        const tableHtml = table ? (table as HTMLElement).outerHTML : '';
        if (table) table.remove();
        const questionHtml = temp.innerHTML;

        let finalHtml = `<div class='question-block'>${questionHtml}</div>`;

        const setResultImage = (row: HTMLElement, imgName: string) => {
            let resultTd = row.querySelector('td.result_td') as HTMLElement | null;
            if (!resultTd) {
                resultTd = doc.createElement('td');
                resultTd.className = 'result_td';
                row.appendChild(resultTd);
            }
            resultTd.innerHTML =
                `<img style='width:20px; height:20px; object-fit:contain;' src='${assetsPath}Image/Questions/EL_L/${imgName}'/>`;
        };

        attempts.forEach((attempt, attemptIndex) => {
            const clone = doc.createElement('div');
            clone.innerHTML = tableHtml;

            const EnteredCorrectAnswer = attempt.EntCorAns || '';
            const EnteredWrongAnswer = attempt.EntWrongAns || '';
            const OptOrder = attempt.OptOrder || '';

            const tbody = clone.querySelector('#KSD_TABLE>tbody');
            const isCorrect = !!EnteredCorrectAnswer && EnteredCorrectAnswer.length > 0 &&
                (!EnteredWrongAnswer || EnteredWrongAnswer.length === 0);

            // step 1: id based row map (JS only knows TR_ ids)
            const rowMap: Record<string, HTMLElement> = {};
            if (tbody) {
                qsa<HTMLElement>(tbody, "tr[id^='TR_']").forEach(r => { rowMap[r.id.replace('TR_', '')] = r; });
                // step 2: clear answer rows
                Object.keys(rowMap).forEach(k => rowMap[k].remove());
                // step 3: apply order
                const orderArr = OptOrder ? OptOrder.split('~') : Object.keys(rowMap);
                orderArr.forEach(idx => { if (rowMap[idx]) tbody.appendChild(rowMap[idx]); });
            }

            // step 4: marking
            const CorrectAnswerIndex = EnteredCorrectAnswer ? EnteredCorrectAnswer.split('~') : [];
            const WrongAnswerIndex = EnteredWrongAnswer ? EnteredWrongAnswer.split('~') : [];
            qsa<HTMLElement>(clone, '#KSD_TABLE>tbody>tr[id^="TR_"]').forEach(row => {
                const rowIndex = row.id.replace('TR_', '');
                if (CorrectAnswerIndex.includes(rowIndex)) setResultImage(row, 'AnsCorrect.GIF');
                else if (WrongAnswerIndex.includes(rowIndex)) setResultImage(row, 'AnsWrong.GIF');
                else setResultImage(row, ''); // JS renders the img even with no icon name
            });

            const headerText = StudentQuestionViewer_headerText(attemptIndex, attempts.length, attempt.AnsweredTime, true);
            const bgColor = isCorrect ? correctBg : wrongBg;
            const innerTable = clone.querySelector('#KSD_TABLE');
            finalHtml +=
                `<div class='attempt-block' style='margin-top:15px;'>` +
                `<div style='background:${bgColor};color:#fff;padding:8px 12px;font-weight:bold;margin-bottom:10px;'>${headerText}</div>` +
                (innerTable ? (innerTable as HTMLElement).outerHTML : '') +
                `</div>`;
        });
        doc.body.innerHTML = finalHtml;
    };
    const MarkAns_N = (doc: Document, attemptsIn: Attempt[]) => {
        window.setTimeout(() => {
            let attempts = attemptsIn;
            const originalHtml = doc.body.innerHTML;
            if (!attempts.length) {
                attempts = [{
                    EntCorAns: getParam('EntCorAns') || '',
                    EntWrongAns: getParam('EntWrongAns') || '',
                    EntBlankAns: getParam('EntBlankAns') || '',
                    SessionId: '',
                }];
            }

            let finalHtml = '';
            attempts.forEach((attempt, attemptIndex) => {
                doc.body.innerHTML = originalHtml;

                const EnteredCorrectAnswer = attempt.EntCorAns || '';
                const EnteredWrongAnswer = attempt.EntWrongAns || '';
                const EnteredBlankAnswer = attempt.EntBlankAns || '';
                const CorrectAnswers = ((doc.getElementById('KSD_BW') as HTMLInputElement | null)?.value || '').split('|');

                const isCorrect = !!EnteredCorrectAnswer &&
                    EnteredCorrectAnswer.split(',')[0] !== '' &&
                    (EnteredWrongAnswer === '' || EnteredWrongAnswer.split(',')[0] === '') &&
                    EnteredBlankAnswer === '';

                if (EnteredCorrectAnswer || EnteredWrongAnswer || EnteredBlankAnswer) {
                    if (EnteredCorrectAnswer.split(',')[0] !== '' && EnteredWrongAnswer.split(',')[0] === '') {
                        StudentQuestionViewer_renderNCorrect(doc, EnteredCorrectAnswer);
                    } else if (EnteredWrongAnswer.split(',')[0] !== '' || EnteredBlankAnswer !== '') {
                        if (EnteredCorrectAnswer !== '') {
                            StudentQuestionViewer_renderNCorrect(doc, EnteredCorrectAnswer);
                        }
                        if (EnteredBlankAnswer !== '') {
                            EnteredBlankAnswer.split(',')[0].split('~').forEach(v => {
                                const idx = parseInt(v, 10); // already a pure index
                                const input = qsa<HTMLInputElement>(doc, 'input[id^="KSD_BLANK_"]')[idx];
                                if (!input) return;
                                input.style.backgroundColor = 'yellow';
                                input.style.border = '1px dashed black';
                                const span = doc.createElement('span');
                                span.style.color = 'green';
                                span.innerHTML = CorrectAnswers[idx] ?? '';
                                input.insertAdjacentElement('afterend', span);
                            });
                        }
                        if (EnteredWrongAnswer !== '') {
                            EnteredWrongAnswer.split(',')[0].split('~').forEach(fullVal => {
                                const idx = parseInt(fullVal.substring(0, fullVal.length - 1), 10);
                                const wrongVal = fullVal.substring(fullVal.length - 1);
                                const input = qsa<HTMLInputElement>(doc, 'input[id^="KSD_BLANK_"]')[idx];
                                if (!input) return;
                                const parent = doc.createElement('span');
                                parent.style.borderBottom = '2px solid red';
                                const spanWrong = doc.createElement('span');
                                spanWrong.style.color = 'red';
                                spanWrong.style.background = 'yellow';
                                spanWrong.style.textDecoration = 'line-through';
                                spanWrong.innerHTML = wrongVal;
                                const spanCorrect = doc.createElement('span');
                                spanCorrect.style.color = 'green';
                                spanCorrect.innerHTML = CorrectAnswers[idx] ?? '';
                                parent.appendChild(spanWrong);
                                parent.insertAdjacentHTML('beforeend', '&nbsp;');
                                parent.appendChild(spanCorrect);
                                input.insertAdjacentElement('afterend', parent);
                                input.style.display = 'none';
                            });
                        }
                    }
                }

                const headerText = StudentQuestionViewer_headerText(attemptIndex, attempts.length, attempt.AnsweredTime, true);
                const bgColor = isCorrect ? correctBg : wrongBg;
                finalHtml +=
                    `<div style='margin-bottom:20px;'>` +
                    `<div style='font-weight:bold;background:${bgColor};color:#fff;padding:8px 12px;margin-bottom:10px;'>${headerText}</div>` +
                    doc.body.innerHTML +
                    `</div>`;
            });
            doc.body.innerHTML = finalHtml;
        }, 400);
    };
    const MarkAns_W = (doc: Document, attempts: Attempt[]) => {
        window.setTimeout(() => {
            doc.getElementById('spAttemptContainer')?.remove();
            if (!attempts.length) return;
            const body = doc.body;
            if (!body) return;

            const container = doc.createElement('div');
            container.id = 'spAttemptContainer';
            container.style.margin = '10px 0';
            const total = attempts.length;

            attempts.forEach((attempt, index) => {
                const entCrt = (attempt.EntCorAns || attempt.EntCrtAns || '').toString().trim();
                const entWrong = (attempt.EntWrongAns || '').toString().trim();
                const isCorrect = !!entCrt && entCrt.toUpperCase() !== 'NULL' && (!entWrong || entWrong.toUpperCase() === 'NULL');
                const bgColor = isCorrect ? correctBg : wrongBg;

                const header = doc.createElement('div');
                if (total > 1) {
                    header.textContent = `Attempt ${index + 1} (AnsweredTime: ${attempt.AnsweredTime})`;
                    Object.assign(header.style, {
                        fontWeight: 'bold', marginTop: '10px', marginBottom: '2px', color: '#fff',
                        background: bgColor, padding: '6px 10px', borderRadius: '4px',
                    });
                } else {
                    header.textContent = `AnsweredTime: ${attempt.AnsweredTime}`;
                    Object.assign(header.style, {
                        marginTop: '10px', marginBottom: '2px', color: '#fff',
                        background: bgColor, padding: '6px 10px', borderRadius: '4px',
                    });
                }
                container.appendChild(header);

                const result = doc.createElement('div');
                result.style.marginLeft = total > 1 ? '14px' : '0px';
                result.style.marginBottom = total > 1 ? '6px' : '3px';
                if (entCrt && entCrt.toUpperCase() !== 'NULL' && total > 1) {
                    result.textContent = `Entered correct answer: ${entCrt}`;
                    result.style.color = 'green';
                } else if (entWrong && entWrong.toUpperCase() !== 'NULL') {
                    result.textContent = `Entered wrong answer: ${entWrong}`;
                    result.style.color = '#c00';
                }
                container.appendChild(result);
            });
            body.appendChild(container);
        }, 500);
    };
    const MarkAns_D = (doc: Document, assetsPath: string, attempts: Attempt[]) => {
        // The legacy function takes these three values from the PAGE querystring.
        const EnteredCorrectAnswer = getParam('EntCorAns') || '';
        const EnteredWrongAnswer = getParam('EntWrongAns') || '';
        const EnteredBlankAnswer = getParam('EntBlankAns') || '';

        window.setTimeout(() => {
            const dropdowns = () => qsa<HTMLSelectElement>(doc, "table select[id='KSD_SELECT']");
            const correctOptionText = (select: HTMLSelectElement) =>
                select.querySelector("option[value='1']")?.textContent || '';

            // ----- 1) PAGE-querystring marking -----
            if (EnteredCorrectAnswer !== '' && EnteredWrongAnswer === '') {
                // Legacy right-answer branch sets only ReturnValue status.
                // There is no DOM mutation here.
            } else if (EnteredWrongAnswer !== '') {
                const CorrectArr = EnteredCorrectAnswer
                    ? EnteredCorrectAnswer.split('~')
                    : [];

                CorrectArr.forEach(item => {
                    const idx = parseInt(item.substring(0, 2), 10);
                    const dropdown = dropdowns()[idx];
                    if (!dropdown) return;

                    const span = doc.createElement('span');
                    span.style.backgroundColor = 'transparent';
                    span.style.color = 'green';
                    span.style.borderBottom = '2px solid red';
                    span.style.margin = '3px';
                    span.innerHTML =
                        `${item.substring(2)}` +
                        `<img src='${assetsPath}Image/Questions/EL_O/AnsCorrect.GIF' style='vertical-align:middle;'/>`;
                    dropdown.insertAdjacentElement('afterend', span);
                    dropdown.style.display = 'none';
                });

                EnteredWrongAnswer.split('~').forEach(item => {
                    const idx = parseInt(item.substring(0, 2), 10);
                    const dropdown = dropdowns()[idx];
                    if (!dropdown) return;

                    const spanWrong = doc.createElement('span');
                    spanWrong.style.color = 'red';
                    spanWrong.style.textDecoration = 'line-through';
                    spanWrong.innerHTML = item.substring(2);

                    const spanCorrect = doc.createElement('span');
                    spanCorrect.style.color = 'green';
                    spanCorrect.innerHTML = correctOptionText(dropdown);

                    const parent = doc.createElement('span');
                    parent.style.borderBottom = '2px solid red';
                    parent.style.margin = '3px';
                    parent.appendChild(spanWrong);
                    parent.insertAdjacentHTML('beforeend', '&nbsp;');
                    parent.appendChild(spanCorrect);

                    dropdown.insertAdjacentElement('afterend', parent);
                    dropdown.style.display = 'none';
                });
            } else if (EnteredCorrectAnswer === '' && EnteredWrongAnswer === '') {
                // Legacy branch ignores EnteredBlankAnswer here.
                void EnteredBlankAnswer;

                dropdowns().forEach(dropdown => {
                    const span = doc.createElement('span');
                    span.style.color = 'green';
                    span.style.borderBottom = '2px solid red';
                    span.style.margin = '3px';
                    span.innerHTML = correctOptionText(dropdown);
                    dropdown.insertAdjacentElement('afterend', span);
                });
            }

            // ----- 2) attempt-history rendering -----
            if (!attempts.length) return;

            doc.getElementById('dropdownAttemptsContainer')?.remove();

            const container = doc.createElement('div');
            container.id = 'dropdownAttemptsContainer';

            const firstTable = doc.querySelector('table') as HTMLElement | null;
            const questionBlock = firstTable
                ? StudentQuestionViewer_clone(firstTable)
                : doc.createElement('div');

            qsa(questionBlock, "img[src*='AnsCorrect'], img[src*='AnsWrong']")
                .forEach(img => img.remove());
            qsa(questionBlock, 'span').forEach(span => span.remove());

            const parseArr = (value?: string) => value ? value.split('~') : [];

            attempts.forEach((attempt, idx) => {
                const correctArr = parseArr(attempt.EntCorAns);
                const wrongArr = parseArr(attempt.EntWrongAns);
                const blankArr = parseArr(attempt.EntBlankAns);

                const block = doc.createElement('div');
                block.style.cssText = 'border-radius:6px;padding:10px';

                const isCorrect =
                    wrongArr.length === 0 &&
                    blankArr.length === 0 &&
                    correctArr.length > 0;

                const header = doc.createElement('div');
                header.style.cssText =
                    `background:${isCorrect ? correctBg : wrongBg};` +
                    'padding:8px 12px;font-weight:bold;margin-bottom:10px;color:white;';
                // Important legacy behavior: both single and multiple attempts
                // use "Attempt n (...)" in this second rendering pass.
                header.textContent =
                    `Attempt ${idx + 1} (AnsweredTime: ${attempt.AnsweredTime})`;
                block.appendChild(header);

                const clone = StudentQuestionViewer_clone(questionBlock);

                qsa<HTMLSelectElement>(clone, "select[id='KSD_SELECT']")
                    .forEach((dropdown, i) => {
                        const correctItem = correctArr.find(
                            a => parseInt(a.substring(0, 2), 10) === i
                        );
                        const wrongItem = wrongArr.find(
                            a => parseInt(a.substring(0, 2), 10) === i
                        );
                        const blankItem = blankArr.find(
                            a => parseInt(a.substring(0, 2), 10) === i
                        );

                        let html = '';

                        if (correctItem) {
                            html =
                                `<span style='color:green'>${correctItem.substring(2)}` +
                                `<img src='${assetsPath}Image/Questions/EL_O/AnsCorrect.GIF' style='margin-left:5px;'>` +
                                `</span>`;
                        } else if (wrongItem) {
                            html =
                                `<span style='color:red;text-decoration:line-through'>${wrongItem.substring(2)}</span> ` +
                                `<span style='color:green'>${correctOptionText(dropdown)}</span>`;
                        } else if (blankItem) {
                            html =
                                `${correctOptionText(dropdown)}` +
                                `<img src='${assetsPath}Image/Questions/EL_O/AnsWrong.GIF' style='margin-left:5px;'>`;
                        }

                        const span = doc.createElement('span');
                        span.style.borderBottom = '2px solid red';
                        span.style.margin = '3px';
                        span.innerHTML = html;
                        dropdown.replaceWith(span);
                    });

                block.appendChild(clone);
                container.appendChild(block);
            });

            if (firstTable) firstTable.style.display = 'none';
            doc.body.appendChild(container);
        }, 500);
    };
    const MarkAns_I = (doc: Document, assetsPath: string, attempts: Attempt[]) => {
        window.setTimeout(() => {
            const EC = getParam('EntCorAns') || '';
            const EW = getParam('EntWrongAns') || '';
            const EB = getParam('EntBlankAns') || '';

            const info = ((doc.getElementById('KSD_MW_Info') as HTMLInputElement | null)?.value || '').split('|');
            const NoOfOptions = parseInt(info[0], 10);
            const WordOptions: string[] = [];
            for (let i = 0; i < NoOfOptions; i++) {
                if (info[i + 1] !== '0N<NEW LINE>') WordOptions.push(info[i + 1]);
            }

            const opts = () => qsa<HTMLElement>(doc, '.word-option');
            const optIdx = (el: HTMLElement) => parseInt((el.id || '').substring(14), 10);
            const toInts = (s: string) => s.split('~').map(v => parseInt(v, 10));
            const okSrc = `${assetsPath}Image/Questions/EL_I/AnsCorrect.png`;
            const badSrc = `${assetsPath}Image/Questions/EL_I/AnsWrong.png`;

            // ---- 1) querystring pass (changes the live options; visible when there are no attempts) ----
            if (EW || EC || EB) {
                const CorrectAnswerIndex = toInts(EC);
                const WrongAnswerIndex = toInts(EW);
                const BlankAnswerIndex = toInts(EB);
                if (EC !== '' && EW === '' && EB === '') {
                    opts().forEach(o => {
                        if (CorrectAnswerIndex.indexOf(optIdx(o)) !== -1) StudentQuestionViewer_markWordOption(o, 'solid 2px #77c84b', okSrc);
                    });
                } else if (EW !== '' || EB !== '') {
                    opts().forEach(o => {
                        const ix = optIdx(o);
                        if (CorrectAnswerIndex.indexOf(ix) !== -1) StudentQuestionViewer_markWordOption(o, 'solid 2px #77c84b', okSrc);
                        else if (WrongAnswerIndex.indexOf(ix) !== -1) StudentQuestionViewer_markWordOption(o, 'solid 2px #e21818', badSrc);
                        else if (BlankAnswerIndex.indexOf(ix) !== -1) StudentQuestionViewer_markWordOption(o, 'solid 2px #757575');
                    });
                }
            } else {
                opts().forEach((o, index) => {
                    if (WordOptions[index] && WordOptions[index].substr(1, 1) === 'Y') {
                        StudentQuestionViewer_markWordOption(o, '2px solid #77c84b');
                    }
                });
            }

            // ---- 2) attempts pass ----
            if (!attempts.length) return;

            doc.getElementById('wordAttemptsContainer')?.remove();
            const container = doc.createElement('div');
            container.id = 'wordAttemptsContainer';

            const firstTable = doc.querySelector('table') as HTMLElement | null;
            const questionBlock = firstTable ? StudentQuestionViewer_clone(firstTable) : doc.createElement('div');
            qsa(questionBlock, "img[src*='AnsCorrect'], img[src*='AnsWrong']").forEach(img => img.remove());

            const parseArr = (s?: string) => (s ? s.split('~').map(v => parseInt(v, 10)) : []);

            attempts.forEach((attempt, idx) => {
                const correctArr = parseArr(attempt.EntCorAns);
                const wrongArr = parseArr(attempt.EntWrongAns);
                const blankArr = parseArr(attempt.EntBlankAns);

                const block = doc.createElement('div');
                block.style.cssText = 'border-radius:6px;padding:10px;margin-bottom:15px';
                const isCorrect = wrongArr.length === 0 && blankArr.length === 0 && correctArr.length > 0;
                const bgColor = isCorrect ? correctBg : wrongBg;

                // both the >1 and ==1 branches of the JS print "Attempt n (...)"
                const header = doc.createElement('div');
                header.style.cssText = `background:${bgColor};padding:8px 12px;font-weight:bold;margin-bottom:10px;color:white;`;
                header.textContent = `Attempt ${idx + 1} (AnsweredTime: ${attempt.AnsweredTime})`;
                block.appendChild(header);

                const clone = StudentQuestionViewer_clone(questionBlock);
                qsa<HTMLElement>(clone, '.word-option').forEach(opt => {
                    const index = parseInt((opt.id || '').substring(14), 10);
                    if (correctArr.indexOf(index) !== -1) {
                        StudentQuestionViewer_markWordOption(opt, '2px solid #77c84b', okSrc);
                    } else if (wrongArr.indexOf(index) !== -1) {
                        StudentQuestionViewer_markWordOption(opt, '2px solid #e21818', badSrc);
                    } else if (blankArr.indexOf(index) !== -1) {
                        StudentQuestionViewer_markWordOption(opt, '2px solid #999999');
                    }
                });
                block.appendChild(clone);
                container.appendChild(block);
            });

            if (firstTable) firstTable.style.display = 'none';
            doc.body.appendChild(container);
        }, 500);
    };

    const prepareQuestionDocument = (doc: Document, qnType: string) => {
        const body = doc.body;
        if (!body) return;
        const type = (qnType || '').trim().toLowerCase();

        StudentQuestionViewer_fixAssetPaths(doc, legacyLessonPath);

        // ---- capture everything BEFORE the body is wiped ----
        const tableQns = qsa<HTMLTableElement>(doc, 'table')
            .filter(t => (t.getAttribute('name') || '').toLowerCase() === 'tableqn');
        tableQns.forEach(t => { t.style.border = '1.4px solid black'; });

        const reasons = qsa<HTMLElement>(doc, '[id^="TD_Rsn_"]').map(n => n.innerHTML.trim());
        const dummywords = qsa<HTMLElement>(doc, '[id^="KSD_WLIST_DragDrop_"]').map(n => n.innerHTML.trim());
        const isDragDropOn = dummywords.length > 0;

        const allInputs = qsa<HTMLInputElement>(doc, 'input');
        const infoInputs = allInputs.filter(i => i.id && i.id.toLowerCase().endsWith('info'));
        const bwInputs = allInputs.filter(i => i.id && i.id.endsWith('BW'));
        const caInputs = allInputs.filter(i => i.id && i.id.endsWith('MW_CA'));
        qnDetailsRef.current = infoInputs.length ? infoInputs[0].value : '';

        const ksdTables = qsa<HTMLElement>(doc, 'table').filter(t => (t.id || '').toLowerCase() === 'ksd_table');
        ksdTables.forEach(t => {
            qsa(t, 'td[id^="KSD_TDANS_"]').forEach(n => n.remove());
            qsa(t, 'td[id^="TD_Rsn_"]').forEach(n => n.remove());
            qsa(t, 'td[id^="TDANS_"]').forEach(n => n.remove());
            if (type !== 'n') StudentQuestionViewer_prepareOptionsTable(t);
        });
        tableQns.forEach(t => { t.style.border = '1.4px solid black'; });

        // <style>/<link rel=stylesheet> that lived in the body are moved to <head>
        qsa<HTMLElement>(body, 'style, link[rel="stylesheet"]').forEach(n => doc.head.appendChild(n));

        // ---- rebuild body (fresh document in the JS: no body attributes) ----
        body.innerHTML = '';
        Array.from(body.attributes).forEach(a => body.removeAttribute(a.name));
        body.style.fontFamily = 'arial, sans-serif';
        // body.style.padding = '10px';

        const mkH4 = (text: string) => {
            const h = doc.createElement('h4');
            h.style.marginBottom = '5px';
            h.textContent = text;
            return h;
        };

        body.appendChild(mkH4('Question Contents:'));
        tableQns.forEach(t => body.appendChild(t));
        body.appendChild(doc.createElement('br'));

        if (['c', 'o', 'm', 'b', 'l'].includes(type)) {
            body.appendChild(mkH4('Options:'));
            ksdTables.forEach(t => body.appendChild(t));
        }

        if (dummywords.length > 1) {
            body.appendChild(mkH4(' Dummy words:'));
            const ul = doc.createElement('ul');
            ul.setAttribute('style', 'margin-top:10px;');
            ul.innerHTML = dummywords.slice(1).map(w => '<li style="margin-right:10px;">' + w + '</li>').join('');
            body.appendChild(ul);
        }

        if ((type === 'o' || type === 'c') && reasons.length > 0) {
            const optionRows: HTMLElement[] = [];
            ksdTables.forEach(t => {
                Array.from(t.children).filter(c => c.tagName === 'TBODY').forEach(tb => {
                    Array.from(tb.children).forEach(tr => {
                        if ((tr as HTMLElement).id.indexOf('KSD_TR_') === 0) optionRows.push(tr as HTMLElement);
                    });
                });
            });
            optionRows.forEach((row, index) => {
                const rsn = reasons[index] || '';
                const tds = Array.from(row.children).filter(c => c.tagName === 'TD') as HTMLElement[];
                if (tds.length === 2) tds[0].style.display = 'none';
                if (tds.length < 3) {
                    const td = doc.createElement('td');
                    td.setAttribute('style', 'padding-left:15px; font-style:italic; vertical-align:middle;');
                    td.innerHTML = rsn;
                    row.appendChild(td);
                } else {
                    tds[2].innerHTML = rsn;
                }
            });
        }

        const caVal = caInputs.length ? caInputs[0].value : '';
        if (caVal && caVal.trim() !== '') {
            const span = doc.createElement('span');
            span.textContent = 'Answer: ' + caVal;
            body.appendChild(span);
        }

        if (type === 'b' && reasons.length > 0) {
            if (reasons[0]) {
                body.insertAdjacentHTML('beforeend',
                    '<br /> <div style="margin-left:10px;"><b>Reason for True:</b> ' + reasons[0] + '</div> <br />');
            }
            if (reasons[1]) {
                body.insertAdjacentHTML('beforeend',
                    '<div style="margin-left:10px;"><b>Reason for False:</b> ' + reasons[1] + '</div>');
            }
        }

        if (type === 'f') {
            const qnInfo = qnDetailsRef.current.split('|');
            const comments = qnInfo.slice(-2);
            const htmlcontents = comments.map((item, i) => ({ letter: i === 0 ? 'A' : 'B', content: item }));
            const correctIndex = parseInt(qnInfo[3], 10) - 1;
            if (correctIndex >= 0 && correctIndex < htmlcontents.length) {
                const correct = htmlcontents.splice(correctIndex, 1)[0];
                htmlcontents.unshift(correct);
            }
            const clean = (c?: string) =>
                c && c.trim() !== '' && c !== '0' && !/^\d$/.test(c.trim()) ? c : '';
            setFExtra({
                correct: clean(htmlcontents[0]?.content),
                wrong: clean(htmlcontents[1]?.content),
                dragDrop: isDragDropOn,
            });
        }

        infoInputs.forEach(i => body.appendChild(i));
        bwInputs.forEach(i => body.appendChild(i));
        caInputs.forEach(i => body.appendChild(i));
    };

    // JS iframe-load handler: type specific Init
    const runInit = (doc: Document, qnType: string, assetsPath: string, attempts: Attempt[]) => {
        switch (qnType.toUpperCase()) {
            case 'B': Init_B(doc); break;
            case 'O': Init_O(doc, assetsPath); break;
            case 'C': Init_C(doc); break;
            case 'M': Init_M(doc, attempts); break;
            case 'F': Init_F(doc); break;
            case 'W': Init_W(doc); break;
            case 'I': Init_I(doc); break;
            case 'L': Init_L(doc, assetsPath, attempts); break;
            case 'D': Init_D(doc); break;
            case 'N': Init_N(doc); break;
            default: break;
        }
    };

    // JS $("#imgOK").trigger("click"): type specific MarkAns (L is commented out there — Init_L does it)
    const runMark = (doc: Document, qnType: string, assetsPath: string, attempts: Attempt[]) => {
        switch (qnType.toUpperCase()) {
            case 'B': MarkAns_B(doc, attempts); break;
            case 'O': MarkAns_O(doc, assetsPath, attempts); break;
            case 'C': MarkAns_C(doc, assetsPath, attempts); break;
            case 'M': MarkAns_M(doc, assetsPath); break;
            case 'W': MarkAns_W(doc, attempts); break;
            case 'I': MarkAns_I(doc, assetsPath, attempts); break;
            case 'D': MarkAns_D(doc, assetsPath, attempts); break;
            case 'F': MarkAns_F(doc, assetsPath, attempts); break;
            case 'N': MarkAns_N(doc, attempts); break;
            default: break;
        }
    };

    const onQuestionLoad = () => {
        const iframe = iframeQnRef.current;
        if (!iframe || !data) return;
        injectPlaySoundBridge(iframe);
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;

        try {
            (doc.defaultView as any).playSound = (_id: string, path: string) => play(path);
        } catch (_) { }
        if (processedDocRef.current === doc) return;
        processedDocRef.current = doc;

        const qnType = data.qn_details?.qn_type || '';
        const assetsPath = data?.blob_assets_path || legacyAssetsPath;
        const attempts = getAttempts();

        prepareQuestionDocument(doc, qnType);
        runInit(doc, qnType, assetsPath, attempts);
        runMark(doc, qnType, assetsPath, attempts);

        injectPlaySoundBridge(iframe);
        StudentQuestionViewer_highlightSearchWords(iframe);

        // Legacy viewer performs one resize attempt at 550ms after MarkAns.
        // Do not add an image-load resize pass here: that makes the React iframe
        // materially taller than the VB viewer and pushes Clues/Principle/Solutions down.
        iframe.style.overflow = 'hidden';
        window.setTimeout(() => StudentQuestionViewer_resizeIframe(iframe), 550);
    };

    const onClueLoad = useCallback(() => {
        const iframe = iframeCluRef.current;
        if (!iframe) return;
        injectPlaySoundBridge(iframe);
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;
        Object.assign(iframe.style, { width: '98.7%', border: '1.4px solid black', marginLeft: '10px' });
        if (doc.body) doc.body.style.backgroundColor = '#F5DEB3';
        qsa(doc, 'div[id^="divShow"]').forEach((div: any) => { div.style.display = 'block'; });
        setTimeout(() => setIframeHeightToBody(iframe), 200);
        StudentQuestionViewer_highlightSearchWords(iframe);
    }, [injectPlaySoundBridge, StudentQuestionViewer_highlightSearchWords]);

    const onPrincipleLoad = useCallback((iframe: HTMLIFrameElement) => {
        injectPlaySoundBridge(iframe);
        setTimeout(() => setIframeHeightToBody(iframe), 200);
        StudentQuestionViewer_highlightSearchWords(iframe);
    }, [injectPlaySoundBridge, StudentQuestionViewer_highlightSearchWords]);

    const onSolutionLoad = useCallback(() => {
        const iframe = iframeSolRef.current;
        if (!iframe) return;

        injectPlaySoundBridge(iframe);

        const applySolution = () => {
            try {
                const doc = iframe.contentDocument || iframe.contentWindow?.document;
                if (!doc?.body) return;

                const body = doc.body;
                if (body.getAttribute('data-student-viewer-solution-transformed') === '1') {
                    return;
                }

                const firstTable = body.querySelector(':scope > table');
                if (!firstTable) return;

                const newTable = doc.createElement('table');
                newTable.setAttribute('data-student-viewer-solution-table', '1');
                newTable.setAttribute('border', '1');
                newTable.setAttribute('width', '100%');
                newTable.setAttribute('cellpadding', '4');
                newTable.setAttribute('cellspacing', '0');

                const rows = Array.from(
                    firstTable.querySelectorAll(':scope > tbody > tr')
                ) as HTMLTableRowElement[];

                rows.forEach(row => {
                    const tds = row.querySelectorAll('td');
                    const sourceTd = tds.length > 1 ? tds[1] : null;
                    if (!sourceTd) return;

                    const contentClone = sourceTd.cloneNode(true) as HTMLElement;
                    qsa<HTMLElement>(contentClone, '*').forEach(el => el.removeAttribute('style'));
                    contentClone.removeAttribute('style');

                    const newRow = doc.createElement('tr');

                    const lhs = doc.createElement('td');
                    lhs.style.verticalAlign = 'top';
                    lhs.innerHTML = contentClone.innerHTML || '';

                    const rhs = doc.createElement('td');
                    rhs.style.verticalAlign = 'top';
                    const ksdDiv = row.querySelector('div[id^="KsdDiv"]');
                    if (ksdDiv) {
                        const ksdClone = ksdDiv.cloneNode(true) as HTMLElement;
                        qsa<HTMLElement>(ksdClone, '*').forEach(el => el.removeAttribute('style'));
                        ksdClone.removeAttribute('style');
                        rhs.innerHTML = ksdClone.outerHTML;
                    }

                    newRow.appendChild(lhs);
                    newRow.appendChild(rhs);
                    newTable.appendChild(newRow);
                });

                body.innerHTML = '';
                body.appendChild(newTable);
                body.setAttribute('data-student-viewer-solution-transformed', '1');

                // Exact second load handler in legacy JS.
                qsa<HTMLElement>(body, 'p').forEach(p => {
                    p.style.margin = '0';
                    p.style.padding = '0';
                });
                body.removeAttribute('bgcolor');
                body.style.width = '98.7%';
                body.style.marginLeft = '10px';
                body.style.marginTop = '0';
                body.style.padding = '0';
                body.style.fontFamily = 'Arial, sans-serif';

                injectPlaySoundBridge(iframe);

                window.setTimeout(() => {
                    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                    if (!iframeDoc?.body) return;
                    const table = iframeDoc.body.querySelector('table[data-student-viewer-solution-table], body > table') as HTMLElement | null;
                    const contentHeight = Math.max(
                        table?.offsetHeight || 0,
                        iframeDoc.body.scrollHeight || 0,
                        iframeDoc.body.offsetHeight || 0
                    );
                    iframe.style.height = Math.max(contentHeight + 20, 40) + 'px';
                    iframe.style.overflow = 'hidden';
                    iframe.setAttribute('scrolling', 'no');
                }, 200);
            } catch (error) {
                console.error('StudentQuestionViewer solution render error:', error);
                setIframeHeightToBody(iframe);
            }
        };

        applySolution();
        window.setTimeout(applySolution, 100);
        window.setTimeout(applySolution, 500);
        StudentQuestionViewer_highlightSearchWords(iframe);
    }, [injectPlaySoundBridge, setIframeHeightToBody, StudentQuestionViewer_highlightSearchWords]);

    // Close behaves like the legacy Close button when this page is opened
    // from Student Performance. window.close() is blocked by browsers when
    // the tab was not opened by script, so fall back to browser history.
// const handleClose = useCallback(() => {
//     if (fromPage === 'EL_Studentperformance') {
//         const current = new URLSearchParams(searchParams);
//         const params = new URLSearchParams();

//         [
//             'SchoolCode', 'FamilyCode', 'Login', 'Country',
//             'DICCode', 'Subject', 'GradeYear', 'Grade', 'page_type'
//         ].forEach(key => {
//             const value = current.get(key);
//             if (value) params.set(key, value);
//         });

//         // Read the return URL first, before anything is cleared.
//         const returnUrl =
//             sessionStorage.getItem('StudentPerformanceReturnUrl') ||
//             sessionStorage.getItem('studentPerformanceUrl') ||
//             `/SUPPORT/EL_Studentperformance?${params.toString()}`;

//         // Legacy Close closes the window. Try that first.
//         window.close();

//         // If the browser blocked it, go back to the report instead.
//         window.setTimeout(() => {
//             if (window.closed) return;
//             sessionStorage.removeItem('StudentPerformanceReturnUrl');
//             sessionStorage.removeItem('studentPerformanceUrl');
//             window.location.href = returnUrl;
//         }, 200);
//         return;
//     }

//     navigate(-1);
// }, [fromPage, navigate, searchParams]);

const handleClose = () => {
  window.close();
};

    // ---- back, mirroring imgbtnBack_Click exactly ----
    const handleBack = () => {
        if (fromPage === 'QuestionInsights') {
            const params = new URLSearchParams();
            params.set('Title', title);
            params.set('Qnno', qnNoStr);
            if (fromlsndate && tolsndate) {
                if (!effectiveDiccode) {
                    params.set('Fromlsndate', fromlsndate);
                    params.set('Tolsndate', tolsndate);
                    navigate(`/SUPPORT/EL_QuestionInsights?${params.toString()}`);
                    return;
                } else if (!testCode) {
                    params.set('DICCode', effectiveDiccode);
                    params.set('Fromlsndate', fromlsndate);
                    params.set('Tolsndate', tolsndate);
                    navigate(`/SUPPORT/EL_QuestionInsights?${params.toString()}`);
                    return;
                } else {
                    params.set('Title', testCode);
                    params.set('LsnCode', title);
                    params.set('DICCode', effectiveDiccode);
                    params.set('Fromlsndate', fromlsndate);
                    params.set('Tolsndate', tolsndate);
                    navigate(`/SUPPORT/EL_QuestionInsights?${params.toString()}`);
                    return;
                }
            }
            if (!testCode) {
                params.set('DICCode', effectiveDiccode);
                navigate(`/SUPPORT/EL_QuestionInsights?${params.toString()}`);
            } else {
                params.set('Title', testCode);
                params.set('LsnCode', title);
                params.set('DICCode', effectiveDiccode);
                navigate(`/SUPPORT/EL_QuestionInsights?${params.toString()}`);
            }
        } else if (country) {
            const mdParam = mdcode ? `&mdcode=${mdcode}` : '';
            navigate(`/LessonViewer?Lessonid=${lessonId}&Country=${country}${mdParam}`);
        }
    };

    const attendedItems = data?.attended_items || [];
    const curLsn = (data?.lesson_code || getParam('LsnCode') || '').toUpperCase();

    const normalizeQn = (q: string) => {
        const n = parseInt(q, 10);
        return Number.isNaN(n) ? q : String(n).padStart(3, '0');
    };
    const normalizedCurrentQn = normalizeQn(qnNoStr);

    const itemIndex = attendedItems.findIndex(
        i => i.lsn_code.toUpperCase() === curLsn && normalizeQn(i.qn_no) === normalizedCurrentQn
    );
    const useItems = !!data?.is_test && itemIndex >= 0;

    const attendedQnNos = (data?.attended_qn_nos || []).map(q => String(q).trim()).filter(Boolean);
    const attendedIndex = useItems
        ? itemIndex
        : attendedQnNos.findIndex(q => normalizeQn(q) === normalizedCurrentQn);
    const attendedCount = useItems ? attendedItems.length : attendedQnNos.length;

    const goToAttended = (idx: number) => {
        const params = new URLSearchParams(searchParams);
        if (useItems) {
            params.set('QnNo', attendedItems[idx].qn_no);
            params.set('LsnCode', attendedItems[idx].lsn_code);
        } else {
            params.set('QnNo', attendedQnNos[idx]);
        }
        params.delete('SessionID');
        window.location.href = `/LessonBrowser/StudentQuestionViewer?${params.toString()}`;
    };

    const goPrev = () => {
        if (insightUrls.length > 0 && insightIndex > 0) {
            sessionStorage.setItem('currentIndex', String(insightIndex - 1));
            window.location.href = insightUrls[insightIndex - 1];
            return;
        }
        if (fromPage === 'EL_Studentperformance' && attendedIndex > 0) goToAttended(attendedIndex - 1);
    };

    const goNext = () => {
        if (insightUrls.length > 0 && insightIndex + 1 < insightUrls.length) {
            sessionStorage.setItem('currentIndex', String(insightIndex + 1));
            window.location.href = insightUrls[insightIndex + 1];
            return;
        }
        if (fromPage === 'EL_Studentperformance' && attendedIndex >= 0 && attendedIndex + 1 < attendedCount) {
            goToAttended(attendedIndex + 1);
        }
    };

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                try {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                } catch (_) { }
            }
        };
    }, [qnNoStr, lessonCode]);

    useEffect(() => {
        if (!data) return;
        const iframe = iframeQnRef.current;
        if (!iframe) return;
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc && doc.readyState === 'complete' && doc.body?.innerHTML.trim()) onQuestionLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const showBack = data?.show_back ?? (fromPage === 'QuestionInsights');
    const showClose = data?.show_close ?? (fromPage === 'EL_Studentperformance');
    const performancePosition = attendedIndex >= 0 ? attendedIndex + 1 : 1;
    const performanceTotal = attendedCount || data?.attended_total || 0;
    // showNext already uses performanceTotal, so it needs no other change
    const showPrev = insightUrls.length > 0
        ? insightIndex > 0
        : fromPage === 'EL_Studentperformance' && attendedIndex > 0;
    const showNext = insightUrls.length > 0
        ? insightIndex + 1 < insightUrls.length
        : fromPage === 'EL_Studentperformance' && attendedIndex >= 0 && attendedIndex + 1 < performanceTotal;
    const showDone = insightUrls.length > 0
        ? insightIndex === insightUrls.length - 1
        : fromPage === 'EL_Studentperformance' && attendedIndex >= 0 && attendedIndex === performanceTotal - 1;
    const noClue = data?.qn_details?.no_clue ?? 0;
    const noPrc = data?.qn_details?.no_prc ?? 0;
    const noSol = data?.qn_details?.no_sol ?? 0;
    const solutionHtml = data?.content?.solution?.trim() || '';
    // The legacy page loads the original solution document and transforms it
    // exactly once from its iframe load handler. Do not pre-transform it here.
    const renderedSolutionHtml = solutionHtml;

    const qnPanelStyle: React.CSSProperties = {
        backgroundColor: '#7a96df',
        verticalAlign: 'middle'
    };

    return (
        <div
            style={{
                width: '100%',
                minHeight: '100vh',
                backgroundColor: '#ffffff',
                fontFamily: 'Arial, sans-serif',
                overflowX: 'hidden'
            }}
        >
            {loading && <Loading />}

            <table style={{ width: '100%' }}>
                <tbody>
                    <tr id="trQnButtons">
                        <td style={{ width: '100%', padding: 0 }}>
                            <table
                                id="tabQnNav"
                                style={{
                                    width: '100%',
                                    padding: 0,
                                    margin: 0,
                                    backgroundColor: '#7a96df',
                                }}
                            >
                                <tbody>
                                    <tr id="trQnOK" style={{ height: '50px', backgroundColor: '#7a96df' }}>
                                        <td
                                            style={{ width: '13%', textAlign: 'left', ...qnPanelStyle }}
                                            className="QnPanel"
                                        >
                                            <GifBtn
                                                name="back"
                                                visible={showBack}
                                                onClick={handleBack}
                                                marginLeft="13px"
                                            />
                                            <GifBtn
                                                name="close"
                                                visible={showClose}
                                                onClick={handleClose}
                                                marginLeft="13px"
                                            />
                                        </td>

                                        <td
                                            style={{
                                                width: '13%',
                                                textAlign: 'center',
                                                display: 'none'
                                            }}
                                            className="QnPanel"
                                        >
                                            <img
                                                id="imgPrc"
                                                src="/Image/principle_n.gif"
                                                alt="principle"
                                                style={{ cursor: 'pointer', verticalAlign: 'middle' }}
                                            />
                                        </td>

                                        <td
                                            style={{
                                                width: '13%',
                                                textAlign: 'center',
                                                padding: 0,
                                                margin: 0,
                                                display: 'none'
                                            }}
                                            className="QnPanel"
                                        >
                                            <img
                                                id="imgClue"
                                                src="/Image/clue_n.gif"
                                                alt="clue"
                                                style={{ cursor: 'pointer', verticalAlign: 'middle' }}
                                            />
                                        </td>

                                        <td
                                            style={{
                                                width: '13%',
                                                textAlign: 'center',
                                                padding: 0,
                                                margin: 0,
                                                display: 'none'
                                            }}
                                            className="QnPanel"
                                        >
                                            <img
                                                id="imgSoln"
                                                src="/Image/solution_n.gif"
                                                alt="solution"
                                                style={{ cursor: 'pointer', verticalAlign: 'middle' }}
                                            />
                                        </td>

                                        <td style={{ width: '2%', ...qnPanelStyle }} className="QnPanel">
                                            &nbsp;
                                        </td>

                                        <td
                                            id="tdprevious"
                                            style={{
                                                width: '31%',
                                                textAlign: 'right',
                                                display: showPrev ? 'table-cell' : 'none'
                                            }}
                                            className="QnPanel"
                                        >
                                            <GifBtn
                                                name="previous"
                                                visible={showPrev}
                                                onClick={goPrev}
                                            />
                                        </td>

                                        <td
                                            id="tdqncount"
                                            style={{
                                                width: '31%',
                                                textAlign: 'right'
                                            }}
                                            className="QnPanel"
                                        >
                                            <span
                                                id="spnqncount"
                                                style={{
                                                    color: 'white',
                                                    textAlign: 'right',
                                                    fontSize: '20px'
                                                }}
                                            >
                                                {insightUrls.length > 0
                                                    ? `Question : ${insightIndex + 1} of ${insightUrls.length}`
                                                    : fromPage === 'EL_Studentperformance' && performanceTotal
                                                        ? `Question : ${performancePosition} of ${performanceTotal}`
                                                        : ''}
                                            </span>
                                        </td>

                                        <td style={{ width: '2%', ...qnPanelStyle }} className="QnPanel">
                                            &nbsp;
                                        </td>

                                        <td
                                            id="tdnext"
                                            style={{
                                                width: '31%',
                                                textAlign: 'right',
                                                display: showNext || showDone ? 'table-cell' : 'none'
                                            }}
                                            className="QnPanel"
                                        >
                                            {showNext ? (
                                                <GifBtn name="next" onClick={goNext} />
                                            ) : (
                                                <GifBtn name="done" visible={showDone} onClick={handleClose} />
                                            )}
                                        </td>

                                        <td
                                            id="tdOK"
                                            style={{
                                                width: '31%',
                                                textAlign: 'right'
                                            }}
                                            className="QnPanel"
                                        >
                                            <img
                                                id="imgOK"
                                                src="/Image/ok_n.gif"
                                                alt="ok"
                                                style={{
                                                    cursor: 'pointer',
                                                    verticalAlign: 'middle',
                                                    display: 'none'
                                                }}
                                            />
                                        </td>

                                        <td style={{ width: '2%', ...qnPanelStyle }} className="QnPanel">
                                            &nbsp;
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>

                    <tr id="trQntitle">
                        <td
                            id="tdQnTitle"
                            style={{
                                padding: '2px',
                                width: '50%',
                                backgroundColor: 'white',
                                fontSize: '16pt',
                                fontFamily: 'Arial, sans-serif',
                                fontWeight: 'bold',
                                lineHeight: '1.2'
                            }}
                            className="HeadMsg"
                        >
                            {effectiveDiccode && (
                                <span
                                    id="spnDiccode"
                                    style={{
                                        color: 'black',
                                        display: 'block',
                                        fontFamily: 'Arial, sans-serif',
                                        fontSize: '16pt',
                                        fontWeight: 'bold',
                                        lineHeight: '1.65',
                                        margin: 0,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    DICCode :{effectiveDiccode}
                                </span>
                            )}

                            {displayTestCode && effectiveDiccode && firstName && (
                                <span
                                    id="spnTestcode"
                                    style={{
                                        color: 'black',
                                        display: 'block',
                                        fontFamily: 'Arial, sans-serif',
                                        fontSize: '16pt',
                                        fontWeight: 'bold',
                                        lineHeight: '1.65',
                                        margin: 0,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Testcode :{displayTestCode}
                                </span>
                            )}

                            {familyCode && (
                                <span
                                    id="spnFamily"
                                    style={{
                                        color: 'black',
                                        display: 'block',
                                        fontFamily: 'Arial, sans-serif',
                                        fontSize: '16pt',
                                        fontWeight: 'bold',
                                        lineHeight: '1.65',
                                        margin: 0,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Family :{familyCode}
                                </span>
                            )}

                            {firstName && (
                                <span
                                    id="spnLogin"
                                    style={{
                                        color: 'black',
                                        display: 'block',
                                        fontFamily: 'Arial, sans-serif',
                                        fontSize: '16pt',
                                        fontWeight: 'bold',
                                        lineHeight: '1.65',
                                        margin: 0,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Firstname :{firstName}
                                </span>
                            )}

                            <br />

                            <span
                                id="spanLessonName"
                                style={{
                                    color: 'maroon',
                                    marginLeft: '4px',
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '16pt',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {data?.lesson_name || lessonCode}
                                {data?.lesson_code ? ` (${data.lesson_code})` : ''}
                            </span>

                            <span
                                style={{
                                    color: 'maroon',
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '16pt',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                &nbsp;&nbsp;-&nbsp;&nbsp;Question
                            </span>

                            &nbsp;

                            <span
                                id="spanCurrentQuestionNo"
                                style={{
                                    color: 'maroon',
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '16pt',
                                    fontWeight: 'bold'
                                }}
                            >
                                {displayQnNo}
                            </span>

                            &nbsp;&nbsp;

                            <span
                                id="spnQnSkipped"
                                style={{
                                    color: '#2f24da',
                                    display:
                                        entCrtAnsParam === 'QnSkipped'
                                            ? 'inline'
                                            : 'none'
                                }}
                            >
                                (Question Skipped)
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>

            <table
                id="tblmain"
                border={0}
                cellSpacing={0}
                cellPadding={0}
                width="100%"
            >
                <tbody>
                    <tr id="triFrame">
                        <td
                            style={{
                                width: '80%',
                                verticalAlign: 'top'
                            }}
                        >
                            <div
                                id="divFrameQn"
                                style={{
                                    WebkitOverflowScrolling: 'touch',
                                    overflow: 'hidden'
                                }}
                            >
                                {data?.content?.question && (
                                    <iframe
                                        ref={iframeQnRef}
                                        id="iFrameQn"
                                        srcDoc={data.content.question}
                                        marginHeight={0}
                                        marginWidth={0}
                                        frameBorder={0}
                                        scrolling="auto"
                                        className="CssiFrm"
                                        title="Question"
                                        style={{
                                            width: '100%',
                                            border: 'none',
                                            display: 'block'
                                        }}
                                        onLoad={onQuestionLoad}
                                    />
                                )}
                            </div>

                            <div
                                id="divFrameClu"
                                style={{
                                    overflow: 'hidden',
                                    display: noClue > 0 && Boolean(data?.content?.clue) ? 'block' : 'none'
                                }}
                            >
                                <h4
                                    style={{
                                        marginLeft: '10px',
                                        fontFamily: 'arial, sans-serif',
                                        marginBottom: '5px'
                                    }}
                                >
                                    Clues
                                </h4>

                                <iframe
                                    ref={iframeCluRef}
                                    id="iFrameClue"
                                    srcDoc={data?.content?.clue || ''}
                                    style={{
                                        width: '100%',
                                        border: 'none',
                                        height: "auto",
                                        overflow: 'auto',
                                        display: noClue > 0 && Boolean(data?.content?.clue) ? 'block' : 'none'
                                    }}
                                    frameBorder={0}
                                    scrolling="no"
                                    onLoad={onClueLoad}
                                    title="Clue"
                                />
                            </div>

                            <div
                                id="divFramePrinciple"
                                style={{
                                    overflow: 'hidden',
                                    display: noPrc > 0 && Boolean(data?.content?.principles?.some(Boolean)) ? 'block' : 'none'
                                }}
                            >
                                <h4
                                    style={{
                                        marginLeft: '10px',
                                        fontFamily: 'arial, sans-serif',
                                        marginBottom: 0
                                    }}
                                >
                                    Principle
                                </h4>

                                {(data?.content?.principles || []).map((p, i) => (
                                    <iframe
                                        key={i}
                                        ref={el => {
                                            prcIframeRefs.current[i] = el;
                                        }}
                                        id={`iFramePrinciple${i + 1}`}
                                        srcDoc={p || ''}
                                        style={{
                                            width: '100%',
                                            border: 'none',
                                            height: "auto",
                                            overflow: 'auto',
                                            display: p ? 'block' : 'none'
                                        }}
                                        frameBorder={0}
                                        scrolling="no"
                                        onLoad={e =>
                                            onPrincipleLoad(e.currentTarget)
                                        }
                                        title={`Principle ${i + 1}`}
                                    />
                                ))}
                            </div>

                            <div
                                id="divFrameSol"
                                style={{
                                    overflow: 'hidden',
                                    display: noSol > 0 && Boolean(solutionHtml) ? 'block' : 'none'
                                }}
                            >
                                <h4
                                    style={{
                                        marginLeft: '10px',
                                        fontFamily: 'arial, sans-serif',
                                        // marginBottom: '5px'
                                    }}
                                >
                                    Solutions
                                </h4>

                                <iframe
                                    ref={iframeSolRef}
                                    id="iFrameSolution"
                                    srcDoc={renderedSolutionHtml || ''}
                                    style={{
                                        width: '100%',
                                        border: 'none',
                                        backgroundColor: 'white',
                                        display: noSol > 0 && Boolean(solutionHtml) ? 'block' : 'none'
                                    }}
                                    frameBorder={0}
                                    scrolling="no"
                                    onLoad={onSolutionLoad}
                                    title="Solution"
                                />
                            </div>
                        </td>

                        <td
                            id="tblrhs"
                            style={{
                                verticalAlign: 'top',
                                marginBottom: "3px",
                                fontSize: '20px',
                                fontFamily: 'Times New Roman, serif',
                                // fontWeight: 'bold',
                                width: '20%'
                            }}
                        >
                            <br />

                            <span>
                                <strong>Type :</strong>
                                <span id="Questiontype">
                                    {data?.qn_details?.type_name || ''}
                                </span>
                            </span>

                            <br />

                            <span>
                                <strong>Complexity :</strong>
                                <span id="Qncomplexity">
                                    {data?.qn_details?.complexity || ''}
                                </span>
                            </span>

                            <br />

                            <span>
                                <strong>Used In Exercise :</strong>
                                <span id="Usedinexe">
                                    {data?.qn_details?.used_in_e || ''}
                                </span>
                            </span>

                            <br />

                            <span>
                                <strong>Used In Test :</strong>
                                <span id="Usedintest">
                                    {data?.qn_details?.used_in_t || ''}
                                </span>
                            </span>

                            {fExtra && (
                                <>
                                    <br />
                                    {fExtra.correct && (
                                        <>
                                            <b>Correct Comment :</b>{' '}
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: fExtra.correct
                                                }}
                                            />
                                            <br />
                                        </>
                                    )}

                                    {fExtra.wrong && (
                                        <>
                                            <b>Wrong Comment :</b>{' '}
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: fExtra.wrong
                                                }}
                                            />
                                            <br />
                                        </>
                                    )}

                                    <b>Drag and drop :</b>{' '}
                                    {fExtra.dragDrop ? 'ON' : 'OFF'}
                                    <br />
                                </>
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );

};

export default StudentQuestionViewer;